<template>
  <Toast />
  <div v-if="isPublishing" class="publishing-overlay" aria-live="polite">
    <div class="publishing-overlay__panel">
      <div class="publishing-overlay__spinner"></div>
      <div class="publishing-overlay__title">Publishing catalogs…</div>
      <div class="publishing-overlay__subtitle">
        Please wait while we finalize the global catalog request.
      </div>
    </div>
  </div>
  <header class="topbar">
    <div class="topbar__left">
      <div class="brand">
        <h1>Local Catalog</h1>
        <p>Discover & govern your data assets</p>
      </div>
    </div>

    <div style="display: flex; justify-content: center">
      <dash-button
        portal-api-url="https://portal-api.ds2.icelab.cloud"
        auth-method="login-required"
      ></dash-button>
    </div>
  </header>
  <!-- <div style="display: flex; justify-content: center">
    <dash-button
      portal-api-url="https://portal-api.ds2.icelab.cloud"
      auth-method="login-required"
    ></dash-button>
  </div> -->

  <div
    class="provider-dashboard-container"
    :class="{
      'sidebar-collapsed': isSidebarCollapsed,
      displayNone: currentView === 'asset',
    }"
  >
    <button
      v-if="isMobile"
      class="mobile-menu-toggle"
      @click="isMobileSidebarOpen = !isMobileSidebarOpen"
    >
      <i class="pi pi-bars"></i>
    </button>

    <Sidebar
      v-model="currentView"
      :collapsed="isSidebarCollapsed"
      :mobile-open="isMobileSidebarOpen"
      :page="'provider'"
      @toggle-collapse="isSidebarCollapsed = !isSidebarCollapsed"
    />

    <main
      class="container"
      :class="{
        displayNone: currentView === 'asset',
      }"
    >
      <div v-if="currentView === 'dashboard'">
        <DashboardView
          v-model:assets="providerAssets"
          :user="user"
          :policies="policies"
          :storeAssets="storeAssets"
          :storeOffers="storeOffers"
          :storePolicies="storePolicies"
          :dlmStoreStatus="dlmStoreStatus"
          :contractDefinitions="contractDefinitions"
          :contractNegotiations="contractNegotiations"
          :contractInfo="contractInfo"
          @open-assets-connector="openProviderAssetsConnector"
          @open-assets-store="openProviderAssetsStore"
          @open-policies-connector="openProviderPoliciesConnector"
          @open-policies-store="openProviderPoliciesStore"
          @open-offers-connector="openProviderOfferConnector"
          @open-offers-store="openProviderOfferStore"
        />
      </div>

      <div v-if="currentView === 'subscriber'">
        <SubscriberView
          v-model:assets="assets"
          :user="user"
          :credentials="credentials"
          :selectedParticipant="selectedParticipant"
          :contractInfo="contractInfo"
          @request-access="openContractModal"
          @toggle-requirements="toggleRequirements"
        />
      </div>

      <div v-if="currentView === 'global-catalogs'">
        <GlobalCatalogsView
          :user="user"
          @refresh-catalogs="loadGlobalCatalogs"
          @request-access="openContractModal"
        />
      </div>

      <div v-if="currentView === 'acquired-assets'">
        <acquiredAssets
          :user="user"
          @refresh-catalogs="loadGlobalCatalogs"
          @request-access="openContractModal"
        />
      </div>

      <div v-if="currentView === 'provider'">
        <provider-view
          :providerAssets="providerAssets"
          :policies="policies"
          :contractDefinitions="contractDefinitions"
        />
      </div>

      <div v-if="currentView === 'provider-assets'">
        <ProviderAssetsView
          :providerAssets="providerAssets"
          :storeAssets="storeAssets"
          :storeOffers="storeOffers"
          :mergedAssets="mergedAssets"
          :user="user"
          :contractDefinitions="contractDefinitions"
          :initial-source="providerAssetsInitialSource"
          @refresh-assets="handleAssetCreated"
          @edit-asset="assetForm"
          @view-asset="viewAssetForm"
          @add-asset="assetForm"
          @asset-created="handleAssetCreated"
        />
      </div>

      <div v-if="currentView === 'provider-policies'">
        <ProviderPoliciesView
          :policies="policies"
          :storePolicies="storePolicies"
          :mergedPolicies="mergedPolicies"
          :user="user"
          :initial-source="providerPoliciesInitialSource"
          @refresh-assets="loadAllPolicies"
        />
      </div>

      <div v-if="currentView === 'provider-offers'">
        <ProviderOffersView
          :providerAssets="providerAssets"
          :policies="policies"
          :storeAssets="storeAssets"
          :user="user"
          :storePolicies="storePolicies"
          :storeOffers="storeOffers"
          :contractDefinitions="contractDefinitions"
          :initial-source="providerOfferInitialSource"
          @refresh-assets="loadAllOffers"
          @publish-asset="openPublishModal"
        />
      </div>

      <div v-if="currentView === 'asset'">
        <AssetRegistration
          v-model="currentView"
          :existingAsset="existingAsset"
          :providerAssets="providerAssets"
          :storeAssets="storeAssets"
          :user="user"
          :objectId="id"
          :view-only="assetViewOnly"
          @asset-created="handleAssetCreated"
        />
      </div>

      <div v-if="currentView === 'consumer'">
        <ConsumerView
          v-model:assets="assets"
          :user="user"
          :credentials="credentials"
          :selectedParticipant="selectedParticipant"
          @request-access="openContractModal"
          @toggle-requirements="toggleRequirements"
        />
      </div>
      <div v-if="currentView === 'global-catalog-ui'">
        <GlobalCatalogUI />
      </div>
    </main>

    <Dialog
      v-model:visible="showCatalogsModal"
      :modal="true"
      :closable="false"
      :dismissableMask="true"
      :closeOnEscape="true"
      class="publish-dialog"
      :style="{ width: '100vw', maxWidth: '1000px' }"
      :breakpoints="{
        '1199px': '95vw',
        '575px': '100vw',
      }"
    >
      <template #header>
        <div class="dialog-header green-header">
          <div class="dialog-header-content">
            <div class="dialog-header__icon">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path
                  d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7"
                />
                <path d="M16 2h6v6" />
                <path d="M21 13v6h-6" />
                <path d="m3 21 9-9" />
              </svg>
            </div>
            <div class="dialog-header__title-section">
              <h2 class="dialog-header__title">Publish to Data Catalogs</h2>
              <p class="dialog-header__subtitle">
                Select where to publish "<span class="asset-name">{{
                  selectedAsset?.name ||
                  selectedAsset?.id?.split("/").pop() ||
                  "Asset"
                }}</span
                >"
              </p>
            </div>
          </div>
        </div>
      </template>

      <div class="dialog-content">
        <!-- Catalog Selection Grid -->
        <div class="catalog-grid">
          <div
            v-for="catalog in availableCatalogs"
            :key="catalog.id"
            class="catalog-card"
            :class="{
              'catalog-card--selected': catalog.selected,
              'catalog-card--local': catalog.type === 'local',
              'catalog-card--global': catalog.type === 'global',
              'catalog-card--disabled': catalog.status === 'disconnected',
            }"
            @click="
              catalog.status !== 'disconnected' &&
                toggleCatalogSelection(catalog.id)
            "
          >
            <div class="catalog-card__header">
              <div class="catalog-card__selection">
                <div
                  class="selection-indicator"
                  :class="{ selected: catalog.selected }"
                >
                  <div v-if="catalog.selected" class="selection-check">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="3"
                    >
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                  </div>
                </div>
              </div>

              <div class="catalog-card__badge" :class="catalog.type">
                {{ catalog.type === "local" ? "Local" : "Global" }}
              </div>
            </div>

            <div class="catalog-card__body">
              <div class="catalog-card__icon">
                <svg
                  v-if="catalog.type === 'local'"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                  <polyline points="9 22 9 12 15 12 15 22" />
                </svg>
                <svg
                  v-else
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="2" y1="12" x2="22" y2="12" />
                  <path
                    d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"
                  />
                </svg>
              </div>

              <h3 class="catalog-card__title">{{ catalog.name }}</h3>
              <p class="catalog-card__description">
                {{ catalog.description }}
              </p>

              <div class="catalog-card__status">
                <span class="status-indicator" :class="catalog.status"></span>
                <span class="status-text">{{
                  catalog.status === "connected" ? "Connected" : "Disconnected"
                }}</span>
              </div>
            </div>

            <div class="catalog-card__footer">
              <div class="catalog-card__endpoint">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  class="endpoint-icon"
                >
                  <circle cx="12" cy="12" r="2" />
                  <path
                    d="M16.24 7.76a6 6 0 0 1 0 8.49m-8.48-.01a6 6 0 0 1 0-8.49m11.31-2.82a10 10 0 0 1 0 14.14m-14.14 0a10 10 0 0 1 0-14.14"
                  />
                </svg>
                <span class="endpoint-text">{{
                  formatEndpoint(catalog.endpoint)
                }}</span>
              </div>
            </div>

            <div
              v-if="catalog.status === 'disconnected'"
              class="catalog-card__overlay"
            >
              <div class="overlay-content">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
                </svg>
                <span>Currently unavailable</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Publishing Summary -->
        <div v-if="selectedCatalogsCount > 0" class="publish-summary">
          <div class="summary-header">
            <h3 class="summary-title">Publishing Summary</h3>
            <span class="summary-count"
              >{{ selectedCatalogsCount }} selected</span
            >
          </div>

          <div class="summary-content">
            <div class="summary-list">
              <div
                v-for="catalog in selectedCatalogs"
                :key="catalog.id"
                class="summary-item"
              >
                <div class="summary-item__icon" :class="catalog.type">
                  <svg
                    v-if="catalog.type === 'local'"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                    <polyline points="9 22 9 12 15 12 15 22" />
                  </svg>
                  <svg
                    v-else
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <line x1="2" y1="12" x2="22" y2="12" />
                    <path
                      d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"
                    />
                  </svg>
                </div>
                <span class="summary-item__name">{{ catalog.name }}</span>
                <span class="summary-item__type">{{ catalog.type }}</span>
              </div>
            </div>

            <div class="summary-actions">
              <p class="summary-hint">
                Assets will be published with the selected access policies and
                metadata
              </p>
            </div>
          </div>
        </div>

        <div
          v-if="publishStep === 'confirm' && selectedGlobalCatalogs.length > 0"
          class="global-metadata-section"
        >
          <div class="summary-header">
            <h3 class="summary-title">Publish Review</h3>
            <span class="summary-count"
              >{{ selectedGlobalCatalogs.length }} global</span
            >
          </div>

          <p class="summary-hint">
            Core metadata is sourced from the selected asset. Global catalog
            defaults come from the DLM store and can be overridden here without
            changing the stored defaults.
          </p>

          <div class="metadata-grid">
            <section class="metadata-panel">
              <div class="metadata-panel__header">
                <h4>Core Metadata</h4>
                <span class="metadata-pill">Read-only</span>
              </div>
              <div class="metadata-table">
                <div
                  v-for="row in coreMetadataRows"
                  :key="row.key"
                  class="metadata-row"
                >
                  <span class="metadata-key">{{ row.label }}</span>
                  <span class="metadata-value">{{ row.value }}</span>
                </div>
                <div v-if="coreMetadataRows.length === 0" class="empty-note">
                  No core metadata available for this asset.
                </div>
              </div>
            </section>

            <section class="metadata-panel metadata-panel--wide">
              <div class="metadata-panel__header">
                <h4>Global Catalog Defaults</h4>
                <span class="metadata-pill metadata-pill--accent"
                  >Editable</span
                >
              </div>

              <div
                v-for="catalog in selectedGlobalCatalogs"
                :key="'metadata-' + catalog.id"
                class="global-metadata-card"
              >
                <div class="global-metadata-card__header">
                  <div>
                    <h4>{{ catalog.name }}</h4>
                    <small>{{ formatEndpoint(catalog.endpoint) }}</small>
                  </div>
                  <span class="metadata-status">
                    {{ globalCatalogSaveStatus[catalog.id] || "Saved" }}
                  </span>
                </div>

                <div
                  v-for="field in globalCatalogDefaults[catalog.id]?.fields ||
                  []"
                  :key="catalog.id + '-' + field.name"
                  class="global-metadata-row"
                  :class="{ 'global-metadata-row--removed': field.removed }"
                >
                  <div class="field-label">
                    <span>{{ field.name }}</span>
                    <small v-if="field.required">Required</small>
                    <button
                      v-else
                      type="button"
                      class="field-remove-btn"
                      @click="toggleFieldRemoval(field)"
                    >
                      {{ field.removed ? "Restore" : "Remove" }}
                    </button>
                  </div>

                  <div class="field-meta">
                    <span v-if="field.removed" class="field-removed-note"
                      >Excluded from payload</span
                    >
                  </div>

                  <select
                    v-if="field.inputType === 'boolean'"
                    v-model="field.overrideValue"
                    class="custom-input"
                    :disabled="field.removed"
                  >
                    <option value="">Use default</option>
                    <option :value="true">true</option>
                    <option :value="false">false</option>
                  </select>

                  <input
                    v-else
                    v-model="field.overrideValue"
                    class="custom-input"
                    :type="field.inputType"
                    placeholder="Override (optional)"
                    :disabled="field.removed"
                  />
                </div>
              </div>
            </section>
          </div>
        </div>

        <div
          v-if="publishStep === 'confirm' && selectedGlobalCatalogs.length > 0"
          class="payload-preview"
        >
          <div class="summary-header">
            <h3 class="summary-title">Global Catalog Payload Preview</h3>
            <span class="summary-count">Live</span>
          </div>
          <pre class="payload-code">{{
            JSON.stringify(globalCatalogPayloadPreview, null, 2)
          }}</pre>
        </div>
      </div>

      <template #footer>
        <div class="dialog-footer">
          <Button
            label="Cancel"
            class="btn btn--outline"
            @click="closeCatalogsModal"
          />
          <Button
            :label="
              publishStep === 'select'
                ? `Review & Publish (${selectedCatalogsCount})`
                : !isPublishing
                ? `Publish to ${selectedCatalogsCount} catalog${
                    selectedCatalogsCount !== 1 ? 's' : ''
                  }`
                : 'Publishing...'
            "
            class="btn btn--primary"
            :disabled="selectedCatalogsCount === 0 || isPublishing"
            @click="handlePublishAction"
          >
            <template #icon>
              <svg
                v-if="!isPublishing && publishStep === 'select'"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                class="btn-icon"
              >
                <path d="M12 5v14M5 12h14" />
              </svg>
              <svg
                v-else-if="!isPublishing && publishStep === 'confirm'"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                class="btn-icon"
              >
                <path d="M5 12l5 5L20 7" />
              </svg>
              <div v-else class="loading-spinner"></div>
            </template>
          </Button>
        </div>
      </template>
    </Dialog>

    <!-- Enhanced Contract Modal -->
    <Dialog
      name="modal"
      v-model:visible="showContractModal"
      :modal="true"
      :closable="false"
      :dismissableMask="true"
      :closeOnEscape="true"
      class="modal-overlay"
      :style="{ width: '80vw', maxWidth: '1000px' }"
      :breakpoints="{
        '1199px': '95vw',
        '575px': '100vw',
      }"
    >
      <div>
        <div class="modal modal-glass modal--xl">
          <div class="modal__header contract-modal-header">
            <div class="modal__title">
              <div
                class="badge pulse"
                style="background-color: #4caf50; color: white"
              >
                <svg class="i" viewBox="0 0 24 24">
                  <path
                    d="M12 2v4M12 18v4M2 12h4M18 12h4M4.9 4.9l2.8 2.8M16.3 16.3l2.8 2.8M4.9 19.1l2.8-2.8M16.3 7.7l2.8-2.8"
                  />
                </svg>
              </div>
              <div>
                <h2 style="color: white">{{ modalTitle }}</h2>
                <p class="muted" style="color: white">
                  Request access to data asset and manage transfer process
                </p>
              </div>
            </div>
            <button
              class="icon-btn icon-btn--ghost"
              @click="closeModal"
              aria-label="Close"
              style="color: black"
            >
              <svg class="i" viewBox="0 0 24 24">
                <path d="M6 6l12 12M6 18L18 6" />
              </svg>
            </button>
          </div>

          <div class="modal__content">
            <!-- Step 1: Contract Details Configuration -->
            <div
              v-if="contractStatus.step === 'configuring'"
              class="contract-configuration"
            >
              <div class="configuration-section">
                <h3 class="section-title" style="color: black">
                  <i class="pi pi-file-contract"></i> Contract Details
                </h3>
                <div class="form-grid">
                  <div class="form-item">
                    <label style="color: black">Asset Name</label>
                    <div class="static-field" style="color: black">
                      {{ currentAsset?.name || currentAsset?.title }}
                    </div>
                  </div>
                  <div class="form-item">
                    <label style="color: black">Asset ID</label>
                    <div class="static-field code" style="color: black">
                      {{ currentAsset?.id }}
                    </div>
                  </div>
                  <div class="form-item">
                    <label style="color: black">Provider</label>
                    <div class="static-field" style="color: black">
                      {{
                        currentAsset?.did ||
                        currentAsset?.["dspace:participantId"] ||
                        "Unknown"
                      }}
                    </div>
                  </div>
                  <div class="form-item">
                    <label style="color: black">Endpoint</label>
                    <div
                      class="static-field code endpoint-url"
                      style="color: black"
                    >
                      {{
                        currentAsset?.endpoint ||
                        currentAsset?.["dcat:service"]?.["dcat:endpointURL"] ||
                        "N/A"
                      }}
                    </div>
                  </div>
                </div>
              </div>

              <!-- Policy Selection (NEW) -->
              <div v-if="hasMultiplePolicies" class="configuration-section">
                <h3 class="section-title" style="color: black">
                  <i class="pi pi-sliders-h"></i> Select Access Policy
                </h3>
                <div class="policy-selection">
                  <div class="policy-options">
                    <div
                      v-for="(policy, index) in currentAsset?.policy || []"
                      :key="index"
                      class="policy-option"
                      :class="{ selected: selectedPolicyIndex === index }"
                      @click="selectPolicy(index)"
                    >
                      <div class="policy-option__header">
                        <div class="policy-option__radio">
                          <div
                            class="radio-dot"
                            :class="{ selected: selectedPolicyIndex === index }"
                          ></div>
                        </div>
                        <div class="policy-option__title">
                          <h4 style="color: black">Policy {{ index + 1 }}</h4>
                          <span class="policy-type" style="color: #666">
                            {{ getPolicyType(policy) }}
                          </span>
                        </div>
                      </div>
                      <div class="policy-option__preview" style="color: black">
                        <div
                          v-if="policy['odrl:permission']"
                          class="policy-details"
                        >
                          <div>
                            <strong>Action:</strong>
                            {{
                              policy["odrl:permission"]["odrl:action"] || "use"
                            }}
                          </div>
                          <div
                            v-if="policy['odrl:permission']['odrl:constraint']"
                          >
                            <div
                              v-for="(constraint, idx) in getConstraints(
                                policy
                              )"
                              :key="idx"
                            >
                              {{ constraint }}
                            </div>
                          </div>
                        </div>
                        <div v-else-if="policy.action" class="policy-details">
                          <div>
                            <strong>Action:</strong> {{ policy.action }}
                          </div>
                          <div v-if="policy.constraint">
                            <div>
                              <strong>Constraint:</strong>
                              {{ formatConstraint(policy.constraint) }}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Required Credentials Check -->
              <div class="configuration-section">
                <h3 class="section-title" style="color: black">
                  <i class="pi pi-shield-check"></i> Credentials Verification
                </h3>
                <div class="credentials-check">
                  <!-- <div
                    v-for="credType in currentAsset?.required_credentials || []"
                    :key="credType"
                    class="credential-item"
                    :class="{
                      valid: hasCredential(credType, currentAsset),
                      missing: !hasCredential(credType),
                    }"
                  >
                    <div class="credential-icon">
                      <i
                        v-if="hasCredential(credType)"
                        class="pi pi-check-circle"
                        style="color: #4caf50"
                      ></i>
                      <i
                        v-else
                        class="pi pi-times-circle"
                        style="color: #f44336"
                      ></i>
                    </div>
                    <div class="credential-details">
                      <span class="credential-name" style="color: black">{{
                        formatCredentialType(credType)
                      }}</span>
                      <span
                        class="credential-status"
                        :style="{
                          color: hasCredential(credType)
                            ? '#4CAF50'
                            : '#f44336',
                        }"
                      >
                        {{
                          hasCredential(credType)
                            ? "Verified"
                            : "Missing - cannot proceed"
                        }}
                      </span>
                    </div>
                    <div
                      v-if="getCredentialConstraint(credType)"
                      class="credential-constraint"
                      style="color: #666"
                    >
                      {{ getCredentialConstraint(credType) }}
                    </div>
                  </div> -->
                </div>
                <div v-if="!acquitionCred" class="warning-message">
                  <i class="pi pi-exclamation-triangle"></i>
                  Some required credentials are missing. You cannot proceed with
                  the contract negotiation until all credentials are verified.
                </div>
                <div v-else class="success-message">
                  <i class="pi pi-check-circle"></i>
                  All required credentials are verified. You can proceed with
                  the contract.
                </div>
              </div>

              <!-- Transfer Configuration -->
              <div class="configuration-section">
                <h3 class="section-title" style="color: black">
                  <i class="pi pi-cloud-download"></i> Transfer Configuration
                </h3>
                <div class="form-grid">
                  <div class="form-item">
                    <label style="color: black">Transfer Protocol</label>
                    <select
                      v-model="transferConfig.protocol"
                      class="custom-input"
                      :disabled="!hasAllCredentials"
                      style="color: black; border-color: #ddd"
                    >
                      <option value="dataspace-protocol-http">
                        Dataspace Protocol HTTP
                      </option>
                      <option value="http-pull">HTTP Pull</option>
                    </select>
                  </div>
                  <div class="form-item">
                    <label style="color: black">Data Destination Type</label>
                    <select
                      v-model="transferConfig.destinationType"
                      class="custom-input"
                      :disabled="!hasAllCredentials"
                      style="color: black; border-color: #ddd"
                    >
                      <option value="HttpProxy">HTTP Proxy</option>
                      <option value="HttpData">HTTP Data</option>
                    </select>
                  </div>
                  <div
                    v-if="showCustomDestination"
                    class="form-item form-item--full"
                  >
                    <label style="color: black">Custom Endpoint URL</label>
                    <input
                      v-model="transferConfig.customEndpoint"
                      type="text"
                      placeholder="https://your-server.com/api/data"
                      class="custom-input"
                      :disabled="!hasAllCredentials"
                      style="color: black; border-color: #ddd"
                    />
                  </div>
                  <div class="form-item">
                    <label style="color: black">Retry Attempts</label>
                    <input
                      v-model="transferConfig.retryAttempts"
                      type="number"
                      min="0"
                      max="10"
                      class="custom-input"
                      :disabled="!hasAllCredentials"
                      style="color: black; border-color: #ddd"
                    />
                  </div>
                </div>
              </div>

              <!-- Policy Preview -->
              <div class="configuration-section">
                <div class="section-header">
                  <h3 class="section-title" style="color: black">
                    <i class="pi pi-eye"></i> Selected Policy Preview
                  </h3>
                  <button
                    class="btn btn-ghost btn-sm"
                    @click="togglePolicyPreview"
                    style="color: #4caf50; border-color: #4caf50"
                  >
                    {{ showPolicyPreview ? "Hide" : "Show" }}
                  </button>
                </div>
                <div
                  v-if="showPolicyPreview && selectedPolicy"
                  class="policy-preview-section"
                >
                  <pre class="code-preview" style="color: black">{{
                    JSON.stringify(selectedPolicy, null, 2)
                  }}</pre>
                </div>
              </div>
            </div>

            <!-- Step 2: Contract Negotiation Progress -->
            <div
              v-if="
                contractStatus.step === 'initiating' ||
                contractStatus.step === 'negotiating'
              "
              class="contract-progress"
            >
              <div class="progress-header">
                <h3 style="color: black">Contract Negotiation in Progress</h3>
                <p class="muted" style="color: black">
                  Establishing secure agreement with data provider
                </p>
              </div>

              <div class="progress-stages">
                <div
                  class="stage"
                  :class="{
                    active: contractStatus.subStep === 'preparing',
                    completed: ['sending', 'waiting', 'verified'].includes(
                      contractStatus.subStep
                    ),
                  }"
                >
                  <div class="stage-icon">
                    <div class="stage-number">1</div>
                    <i class="pi pi-file-edit"></i>
                  </div>
                  <div class="stage-content">
                    <h4 style="color: black">Preparing Contract Request</h4>
                    <p style="color: #666">
                      Building contract offer with access policies
                    </p>
                    <div
                      v-if="contractStatus.subStep === 'preparing'"
                      class="stage-details"
                    >
                      <div class="detail-item">
                        <span style="color: #666">Asset ID:</span>
                        <code style="color: black">{{ currentAsset?.id }}</code>
                      </div>
                      <div class="detail-item">
                        <span style="color: #666">Policy Selected:</span>
                        <code style="color: black"
                          >Policy {{ selectedPolicyIndex + 1 }}</code
                        >
                      </div>
                    </div>
                  </div>
                  <div class="stage-status">
                    <span
                      v-if="contractStatus.subStep === 'preparing'"
                      class="status-badge processing"
                      style="
                        background-color: #e3f2fd;
                        color: #1976d2;
                        border-color: #1976d2;
                      "
                    >
                      <i class="pi pi-spinner pi-spin"></i> Processing
                    </span>
                    <span
                      v-else-if="
                        ['sending', 'waiting', 'verified'].includes(
                          contractStatus.subStep
                        )
                      "
                      class="status-badge completed"
                      style="
                        background-color: #e8f5e8;
                        color: #4caf50;
                        border-color: #4caf50;
                      "
                    >
                      <i class="pi pi-check"></i> Complete
                    </span>
                  </div>
                </div>

                <div
                  class="stage"
                  :class="{
                    active: contractStatus.subStep === 'sending',
                    completed: ['waiting', 'verified'].includes(
                      contractStatus.subStep
                    ),
                  }"
                >
                  <div class="stage-icon">
                    <div class="stage-number">2</div>
                    <i class="pi pi-send"></i>
                  </div>
                  <div class="stage-content">
                    <h4 style="color: black">Sending to Provider</h4>
                    <p style="color: #666">
                      Transmitting contract request to provider system
                    </p>
                    <div
                      v-if="contractStatus.subStep === 'sending'"
                      class="stage-details"
                    >
                      <div class="detail-item">
                        <span style="color: #666">Provider:</span>
                        <span style="color: black">{{
                          currentAsset?.did
                        }}</span>
                      </div>
                      <div class="detail-item">
                        <span style="color: #666">Endpoint:</span>
                        <code class="endpoint" style="color: black">{{
                          formatEndpoint(currentAsset?.endpoint)
                        }}</code>
                      </div>
                    </div>
                  </div>
                  <div class="stage-status">
                    <span
                      v-if="contractStatus.subStep === 'sending'"
                      class="status-badge processing"
                      style="
                        background-color: #e3f2fd;
                        color: #1976d2;
                        border-color: #1976d2;
                      "
                    >
                      <i class="pi pi-spinner pi-spin"></i> Sending
                    </span>
                    <span
                      v-else-if="
                        ['waiting', 'verified'].includes(contractStatus.subStep)
                      "
                      class="status-badge completed"
                      style="
                        background-color: #e8f5e8;
                        color: #4caf50;
                        border-color: #4caf50;
                      "
                    >
                      <i class="pi pi-check"></i> Sent
                    </span>
                    <span v-else class="status-badge pending">
                      <i class="pi pi-clock"></i> Pending
                    </span>
                  </div>
                </div>

                <div
                  class="stage"
                  :class="{
                    active: contractStatus.subStep === 'waiting',
                    completed: contractStatus.subStep === 'verified',
                  }"
                >
                  <div class="stage-icon">
                    <div class="stage-number">3</div>
                    <i class="pi pi-hourglass"></i>
                  </div>
                  <div class="stage-content">
                    <h4 style="color: black">Waiting for Response</h4>
                    <p style="color: #666">
                      Provider is reviewing and responding to the contract
                    </p>
                    <div
                      v-if="contractStatus.subStep === 'waiting'"
                      class="stage-details"
                    >
                      <div class="detail-item">
                        <span style="color: #666">Negotiation ID:</span>
                        <code style="color: black">{{
                          contractStatus.negotiationId
                        }}</code>
                      </div>
                      <div class="detail-item">
                        <span style="color: #666">Status:</span>
                        <span class="status-text" style="color: #ff9800">{{
                          contractStatus.state || "Waiting..."
                        }}</span>
                      </div>
                      <div v-if="contractStatus.lastCheck" class="detail-item">
                        <span style="color: #666">Last checked:</span>
                        <span style="color: black">{{
                          formatTime(contractStatus.lastCheck)
                        }}</span>
                      </div>
                    </div>
                  </div>
                  <div class="stage-status">
                    <span
                      v-if="contractStatus.subStep === 'waiting'"
                      class="status-badge processing"
                      style="
                        background-color: #e3f2fd;
                        color: #1976d2;
                        border-color: #1976d2;
                      "
                    >
                      <i class="pi pi-spinner pi-spin"></i> Waiting
                    </span>
                    <span
                      v-else-if="contractStatus.subStep === 'verified'"
                      class="status-badge completed"
                      style="
                        background-color: #e8f5e8;
                        color: #4caf50;
                        border-color: #4caf50;
                      "
                    >
                      <i class="pi pi-check"></i> Approved
                    </span>
                    <span v-else class="status-badge pending">
                      <i class="pi pi-clock"></i> Pending
                    </span>
                  </div>
                </div>

                <div
                  class="stage"
                  :class="{
                    active: contractStatus.subStep === 'verified',
                  }"
                >
                  <div class="stage-icon">
                    <div class="stage-number">4</div>
                    <i class="pi pi-check-circle"></i>
                  </div>
                  <div class="stage-content">
                    <h4 style="color: black">Contract Verified</h4>
                    <p style="color: #666">
                      Contract agreement finalized and ready for transfer
                    </p>
                    <div
                      v-if="contractStatus.subStep === 'verified'"
                      class="stage-details"
                    >
                      <div class="detail-item">
                        <span style="color: #666">Agreement ID:</span>
                        <code style="color: black">{{
                          contractStatus.contractAgreementId
                        }}</code>
                      </div>
                      <div class="detail-item">
                        <span style="color: #666">Expires:</span>
                        <span style="color: black"
                          >Upon completion of transfer</span
                        >
                      </div>
                    </div>
                  </div>
                  <div class="stage-status">
                    <span
                      v-if="contractStatus.subStep === 'verified'"
                      class="status-badge completed"
                      style="
                        background-color: #e8f5e8;
                        color: #4caf50;
                        border-color: #4caf50;
                      "
                    >
                      <i class="pi pi-check"></i> Finalized
                    </span>
                    <span v-else class="status-badge pending">
                      <i class="pi pi-clock"></i> Pending
                    </span>
                  </div>
                </div>
              </div>

              <div class="progress-controls">
                <div class="progress-info">
                  <div class="info-item">
                    <i class="pi pi-info-circle" style="color: #1976d2"></i>
                    <span style="color: black"
                      >This may take a few moments depending on provider
                      response time</span
                    >
                  </div>
                  <div class="info-item">
                    <i class="pi pi-history" style="color: #1976d2"></i>
                    <span style="color: black"
                      >Started: {{ formatTime(contractStatus.startTime) }}</span
                    >
                  </div>
                </div>
                <button
                  class="btn btn-ghost"
                  @click="cancelNegotiation"
                  :disabled="cancellingNegotiation"
                  style="color: #f44336; border-color: #f44336"
                >
                  <span v-if="!cancellingNegotiation">
                    <i class="pi pi-times"></i> Cancel
                  </span>
                  <span v-else>
                    <i class="pi pi-spinner pi-spin"></i> Cancelling...
                  </span>
                </button>
              </div>
            </div>

            <!-- Step 3: Transfer Progress -->
            <div
              v-if="
                contractStatus.step === 'transferring' ||
                contractStatus.step === 'downloading'
              "
              class="transfer-progress"
            >
              <div class="progress-header">
                <h3 style="color: black">Data Transfer in Progress</h3>
                <p class="muted" style="color: black">
                  Retrieving data from provider system
                </p>
              </div>

              <div class="transfer-stages">
                <div
                  class="stage"
                  :class="{
                    active: contractStatus.transferSubStep === 'initiating',
                    completed: ['preparing', 'fetching', 'receiving'].includes(
                      contractStatus.transferSubStep
                    ),
                  }"
                >
                  <div class="stage-icon">
                    <div class="stage-number">1</div>
                    <i class="pi pi-play"></i>
                  </div>
                  <div class="stage-content">
                    <h4 style="color: black">Initiating Transfer</h4>
                    <p style="color: #666">
                      Setting up transfer process with provider
                    </p>
                    <div
                      v-if="contractStatus.transferSubStep === 'initiating'"
                      class="stage-details"
                    >
                      <div class="detail-item">
                        <span style="color: #666">Transfer Type:</span>
                        <span style="color: black">{{
                          transferConfig.protocol
                        }}</span>
                      </div>
                    </div>
                  </div>
                  <div class="stage-status">
                    <span
                      v-if="contractStatus.transferSubStep === 'initiating'"
                      class="status-badge processing"
                      style="
                        background-color: #e3f2fd;
                        color: #1976d2;
                        border-color: #1976d2;
                      "
                    >
                      <i class="pi pi-spinner pi-spin"></i> Starting
                    </span>
                    <span
                      v-else-if="
                        ['preparing', 'fetching', 'receiving'].includes(
                          contractStatus.transferSubStep
                        )
                      "
                      class="status-badge completed"
                      style="
                        background-color: #e8f5e8;
                        color: #4caf50;
                        border-color: #4caf50;
                      "
                    >
                      <i class="pi pi-check"></i> Complete
                    </span>
                  </div>
                </div>

                <div
                  class="stage"
                  :class="{
                    active: contractStatus.transferSubStep === 'preparing',
                    completed: ['fetching', 'receiving'].includes(
                      contractStatus.transferSubStep
                    ),
                  }"
                >
                  <div class="stage-icon">
                    <div class="stage-number">2</div>
                    <i class="pi pi-wrench"></i>
                  </div>
                  <div class="stage-content">
                    <h4 style="color: black">Preparing Data Connection</h4>
                    <p style="color: #666">Establishing secure data channel</p>
                    <div
                      v-if="contractStatus.transferSubStep === 'preparing'"
                      class="stage-details"
                    >
                      <div class="detail-item">
                        <span style="color: #666">Transfer ID:</span>
                        <code style="color: black">{{
                          contractStatus.transferId
                        }}</code>
                      </div>
                      <div class="detail-item">
                        <span style="color: #666">Destination:</span>
                        <span style="color: black">{{
                          transferConfig.destinationType
                        }}</span>
                      </div>
                    </div>
                  </div>
                  <div class="stage-status">
                    <span
                      v-if="contractStatus.transferSubStep === 'preparing'"
                      class="status-badge processing"
                      style="
                        background-color: #e3f2fd;
                        color: #1976d2;
                        border-color: #1976d2;
                      "
                    >
                      <i class="pi pi-spinner pi-spin"></i> Preparing
                    </span>
                    <span
                      v-else-if="
                        ['fetching', 'receiving'].includes(
                          contractStatus.transferSubStep
                        )
                      "
                      class="status-badge completed"
                      style="
                        background-color: #e8f5e8;
                        color: #4caf50;
                        border-color: #4caf50;
                      "
                    >
                      <i class="pi pi-check"></i> Ready
                    </span>
                    <span v-else class="status-badge pending">
                      <i class="pi pi-clock"></i> Pending
                    </span>
                  </div>
                </div>

                <div
                  class="stage"
                  :class="{
                    active: contractStatus.transferSubStep === 'fetching',
                    completed: contractStatus.transferSubStep === 'receiving',
                  }"
                >
                  <div class="stage-icon">
                    <div class="stage-number">3</div>
                    <i class="pi pi-cloud-download"></i>
                  </div>
                  <div class="stage-content">
                    <h4 style="color: black">Fetching Data</h4>
                    <p style="color: #666">
                      Retrieving data from provider endpoint
                    </p>
                    <div
                      v-if="contractStatus.transferSubStep === 'fetching'"
                      class="stage-details"
                    >
                      <div class="progress-bar-container">
                        <div class="progress-bar">
                          <div
                            class="progress-fill"
                            :style="{
                              width: transferProgress + '%',
                              backgroundColor: '#4CAF50',
                            }"
                          ></div>
                        </div>
                        <span class="progress-text" style="color: black"
                          >{{ transferProgress }}%</span
                        >
                      </div>
                      <div v-if="transferSpeed" class="detail-item">
                        <span style="color: #666">Speed:</span>
                        <span style="color: black">{{ transferSpeed }}</span>
                      </div>
                      <div v-if="transferSize" class="detail-item">
                        <span style="color: #666">Size:</span>
                        <span style="color: black">{{ transferSize }}</span>
                      </div>
                    </div>
                  </div>
                  <div class="stage-status">
                    <span
                      v-if="contractStatus.transferSubStep === 'fetching'"
                      class="status-badge processing"
                      style="
                        background-color: #e3f2fd;
                        color: #1976d2;
                        border-color: #1976d2;
                      "
                    >
                      <i class="pi pi-spinner pi-spin"></i> Downloading
                    </span>
                    <span
                      v-else-if="contractStatus.transferSubStep === 'receiving'"
                      class="status-badge completed"
                      style="
                        background-color: #e8f5e8;
                        color: #4caf50;
                        border-color: #4caf50;
                      "
                    >
                      <i class="pi pi-check"></i> Complete
                    </span>
                    <span v-else class="status-badge pending">
                      <i class="pi pi-clock"></i> Pending
                    </span>
                  </div>
                </div>

                <div
                  class="stage"
                  :class="{
                    active: contractStatus.transferSubStep === 'receiving',
                  }"
                >
                  <div class="stage-icon">
                    <div class="stage-number">4</div>
                    <i class="pi pi-database"></i>
                  </div>
                  <div class="stage-content">
                    <h4 style="color: black">Processing Data</h4>
                    <p style="color: #666">
                      Validating and storing received data
                    </p>
                    <div
                      v-if="contractStatus.transferSubStep === 'receiving'"
                      class="stage-details"
                    >
                      <div class="detail-item">
                        <span style="color: #666">Data Format:</span>
                        <span style="color: black">JSON</span>
                      </div>
                      <div class="detail-item">
                        <span style="color: #666">Status:</span>
                        <span class="status-text" style="color: #ff9800"
                          >Processing...</span
                        >
                      </div>
                    </div>
                  </div>
                  <div class="stage-status">
                    <span
                      v-if="contractStatus.transferSubStep === 'receiving'"
                      class="status-badge processing"
                      style="
                        background-color: #e3f2fd;
                        color: #1976d2;
                        border-color: #1976d2;
                      "
                    >
                      <i class="pi pi-spinner pi-spin"></i> Processing
                    </span>
                    <span v-else class="status-badge pending">
                      <i class="pi pi-clock"></i> Pending
                    </span>
                  </div>
                </div>
              </div>

              <div class="transfer-controls">
                <div class="transfer-info">
                  <div class="info-item">
                    <i class="pi pi-shield" style="color: #4caf50"></i>
                    <span style="color: black"
                      >Transfer is encrypted and secure</span
                    >
                  </div>
                  <div v-if="transferEstimatedTime" class="info-item">
                    <i class="pi pi-clock" style="color: #ff9800"></i>
                    <span style="color: black"
                      >Estimated time remaining:
                      {{ transferEstimatedTime }}</span
                    >
                  </div>
                </div>
                <button
                  class="btn btn-ghost"
                  @click="pauseTransfer"
                  :disabled="pausingTransfer || transferPaused"
                  style="color: #ff9800; border-color: #ff9800"
                >
                  <span v-if="transferPaused">
                    <i class="pi pi-play"></i> Resume
                  </span>
                  <span v-else-if="pausingTransfer">
                    <i class="pi pi-spinner pi-spin"></i> Pausing...
                  </span>
                  <span v-else> <i class="pi pi-pause"></i> Pause </span>
                </button>
              </div>
            </div>

            <!-- Step 4: Completion -->
            <div
              v-if="contractStatus.step === 'completed'"
              class="completion-view"
            >
              <div class="completion-header">
                <div class="success-icon" style="color: #4caf50">
                  <i class="pi pi-check-circle"></i>
                </div>
                <h3 style="color: black">Data Transfer Complete!</h3>
                <p class="muted" style="color: black">
                  Successfully retrieved data from the provider
                </p>
              </div>

              <div class="completion-details">
                <div class="detail-card">
                  <h4 style="color: black">
                    <i class="pi pi-cloud-download"></i> Transfer Summary
                  </h4>
                  <div class="detail-grid">
                    <div class="detail-item">
                      <span class="label" style="color: #666">Status:</span>
                      <span
                        class="value status-completed"
                        style="color: #4caf50"
                        >{{ contractStatus.state }}</span
                      >
                    </div>
                    <div class="detail-item">
                      <span class="label" style="color: #666">Protocol:</span>
                      <span class="value" style="color: black">{{
                        transferConfig.protocol
                      }}</span>
                    </div>
                    <div class="detail-item">
                      <span class="label" style="color: #666"
                        >Completion Time:</span
                      >
                      <span class="value" style="color: black">{{
                        formatTime(contractStatus.completionTime)
                      }}</span>
                    </div>
                    <div class="detail-item">
                      <span class="label" style="color: #666">Duration:</span>
                      <span class="value" style="color: black">{{
                        calculateDuration()
                      }}</span>
                    </div>
                  </div>
                </div>

                <div v-if="retrievedData" class="data-preview-section">
                  <div class="section-header">
                    <h4 style="color: black">
                      <i class="pi pi-eye"></i> Data Preview
                    </h4>
                    <div class="data-stats">
                      <span class="stat" style="color: #666">
                        <i class="pi pi-table"></i>
                        {{ getDataRowCount() }} rows
                      </span>
                      <span class="stat" style="color: #666">
                        <i class="pi pi-list"></i>
                        {{ getDataColumnCount() }} columns
                      </span>
                    </div>
                  </div>
                  <json-data-viewer
                    :data="retrievedData"
                    :rows-per-page="10"
                    :page-size-options="[5, 10, 25, 50]"
                  />
                  <div class="data-actions">
                    <button
                      class="btn btn-ghost"
                      @click="downloadData"
                      style="color: #1976d2; border-color: #1976d2"
                    >
                      <i class="pi pi-download"></i> Download JSON
                    </button>
                    <button
                      class="btn btn-ghost"
                      @click="copyDataToClipboard"
                      v-tooltip="'Copy data to clipboard'"
                      style="color: #1976d2; border-color: #1976d2"
                    >
                      <i class="pi pi-copy"></i> Copy
                    </button>
                  </div>
                </div>
              </div>

              <div class="completion-actions">
                <button
                  class="btn btn-ghost"
                  @click="closeModal"
                  style="color: #666; border-color: #ddd"
                >
                  <i class="pi pi-times"></i> Close
                </button>
                <!-- <button
                  class="btn btn-primary"
                  @click="changeState"
                  v-if="canStartNewTransfer"
                  style="
                    background-color: #1976d2;
                    color: white;
                    border-color: #1976d2;
                  "
                >
                  <i class="pi pi-refresh"></i> Complete Transfer Process
                </button> -->
                <!-- <button
                  class="btn btn-primary"
                  @click="fetchTransferData"
                  style="
                    background-color: #4caf50;
                    color: white;
                    border-color: #4caf50;
                  "
                >
                  <i class="pi pi-external-link"></i> Initiate another transfer
                </button> -->
              </div>
            </div>

            <!-- Error State -->
            <div v-if="contractStatus.step === 'error'" class="error-view">
              <div class="error-header">
                <div class="error-icon" style="color: #f44336">
                  <i class="pi pi-times-circle"></i>
                </div>
                <h3 style="color: black">Transfer Failed</h3>
                <p class="muted" style="color: black">
                  {{ contractStatus.message }}
                </p>
              </div>

              <div class="error-details">
                <div class="error-card">
                  <h4 style="color: black">
                    <i class="pi pi-info-circle"></i> Error Details
                  </h4>
                  <pre class="error-message" style="color: #f44336">{{
                    contractStatus.error
                  }}</pre>
                </div>

                <div class="troubleshooting">
                  <h4 style="color: black">
                    <i class="pi pi-wrench"></i> Troubleshooting
                  </h4>
                  <ul>
                    <li
                      v-if="contractStatus.troubleshooting"
                      style="color: #666"
                    >
                      {{ contractStatus.troubleshooting }}
                    </li>
                    <li style="color: #666">Check your network connection</li>
                    <li style="color: #666">
                      Verify the provider service is running
                    </li>
                    <li style="color: #666">
                      Ensure all required credentials are valid
                    </li>
                    <li style="color: #666">
                      Check if the asset is still available
                    </li>
                  </ul>
                </div>
              </div>

              <div class="error-actions">
                <button
                  class="btn btn-ghost"
                  @click="closeModal"
                  style="color: #666; border-color: #ddd"
                >
                  <i class="pi pi-times"></i> Close
                </button>
                <button
                  class="btn btn-primary"
                  @click="retryTransfer"
                  :disabled="retrying"
                  style="
                    background-color: #1976d2;
                    color: white;
                    border-color: #1976d2;
                  "
                >
                  <span v-if="!retrying">
                    <i class="pi pi-refresh"></i> Retry
                  </span>
                  <span v-else>
                    <i class="pi pi-spinner pi-spin"></i> Retrying...
                  </span>
                </button>
                <button
                  class="btn btn-ghost"
                  @click="reportIssue"
                  v-if="canReportIssue"
                  style="color: #ff9800; border-color: #ff9800"
                >
                  <i class="pi pi-flag"></i> Report Issue
                </button>
              </div>
            </div>

            <!-- Action Buttons (Visible in all states except error/completed) -->
            <div
              v-if="
                contractStatus.step !== 'completed' &&
                contractStatus.step !== 'error'
              "
              class="modal__footer"
            >
              <div class="footer-actions">
                <button
                  class="btn btn-ghost"
                  @click="previousStep"
                  :disabled="isPreviousDisabled"
                  style="color: #666; border-color: #ddd"
                >
                  <i class="pi pi-arrow-left"></i> Back
                </button>
                <div class="action-spacer"></div>
                <button
                  class="btn btn-ghost"
                  @click="closeModal"
                  :disabled="isCloseDisabled"
                  style="color: #f44336; border-color: #f44336"
                >
                  Cancel
                </button>
                <button
                  class="btn btn-primary"
                  @click="nextStep"
                  :disabled="isNextDisabled"
                  style="
                    background-color: #4caf50;
                    color: white;
                    border-color: #4caf50;
                  "
                >
                  <span v-if="!isLoadingNext">
                    {{ getNextButtonText() }}
                  </span>
                  <span v-else class="inline-loading">
                    <div class="spinner spinner--sm"></div>
                    {{ getLoadingText() }}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, computed, getCurrentInstance } from "vue";
import axios from "axios";
const { appContext } = getCurrentInstance();
const API_BASE_URL = appContext.config.globalProperties.$apiBase;

import ProviderView from "./ProviderView.vue";
import ProviderAssetsView from "./ProviderAssetsView.vue";
import ProviderPoliciesView from "./ProviderPoliciesView.vue";
import ProviderOffersView from "./ProviderOffersView.vue";
import AssetRegistration from "./AssetRegistration.vue";
import { userService } from "../services/UserService";
import SubscriberView from "./SubscriberView.vue";
import ConsumerView from "./ConsumerView.vue";
import GlobalCatalogUI from "./GlobalCatalogUI.vue";
import GlobalCatalogsView from "./GlobalCatalogsView.vue";
import Sidebar from "../component/Sidebar.vue";
import DashboardView from "./DashboardView.vue";
import acquiredAssets from "./acquiredAssets.vue";
import Dialog from "primevue/dialog";
import Toast from "primevue/toast";
import Button from "primevue/button";
import { useToast } from "primevue/usetoast";

const toast = useToast();

// State
const currentView = ref("dashboard");
const retrievedData = ref(null);
const credentials = ref([]);
const assets = ref([]);
const providerAssets = ref([]);
const dlmStoreStatus = ref({
  state: "unknown",
  message: "Checking",
  lastChecked: null,
});
const providerAssetsSourceOverride = ref(null);
const providerAssetsInitialSource = computed(
  () => providerAssetsSourceOverride.value || "all"
);
const providerPoliciesSourceOverride = ref(null);
const providerPoliciesInitialSource = computed(
  () => providerPoliciesSourceOverride.value || "all"
);
const providerOfferSourceOverride = ref(null);
const providerOfferInitialSource = computed(
  () => providerOfferSourceOverride.value || "all"
);
const contractDefinitions = ref([]);
const policies = ref([]);
const membershipPolicy = ref([]);
const storeSelectedPolicy = ref([]);
const contractNegotiations = ref([]);
const selectedOffer = ref([]);
const contractInfo = ref([]);
const selectedParticipant = ref("did:web:consumer-identityhub%3A7083:consumer");
const participants = ref([
  {
    id: "did:web:consumer-identityhub%3A7083:consumer",
    name: "Default Consumer",
  },
]);

const loading = ref({
  credentials: false,
  assets: false,
  contract: false,
  participants: false,
});

const assetShowReq = ref(null);

watch(currentView, (view) => {
  if (view !== "provider-assets" && providerAssetsSourceOverride.value) {
    providerAssetsSourceOverride.value = null;
  }
  if (view !== "provider-policies" && providerPoliciesSourceOverride.value) {
    providerPoliciesSourceOverride.value = null;
  }

  if (view !== "provider-offers" && providerOfferSourceOverride.value) {
    providerOfferSourceOverride.value = null;
  }
});

// Enhanced Contract Modal State
const showContractModal = ref(false);
const modalTitle = ref("Contract Negotiation");
const currentAsset = ref(null);
const existingAsset = ref(null);
const assetViewOnly = ref(false);
const selectedPolicyIndex = ref(0); // Track selected policy
const transferConfig = ref({
  protocol: "dataspace-protocol-http",
  destinationType: "HttpProxy",
  customEndpoint: "",
  retryAttempts: 3,
});
const contractStatus = ref({
  step: "configuring", // configuring, initiating, negotiating, transferring, downloading, completed, error
  subStep: "preparing", // preparing, sending, waiting, verified
  transferSubStep: "initiating", // initiating, preparing, fetching, receiving
  message: "",
  negotiationId: null,
  contractAgreementId: null,
  transferId: null,
  error: null,
  troubleshooting: null,
  startTime: null,
  lastCheck: null,
  completionTime: null,
  state: null,
  policyId: null,
});
const transferProgress = ref(0);
const transferSpeed = ref(null);
const transferSize = ref(null);
const transferEstimatedTime = ref(null);
const transferPaused = ref(false);
const showPolicyPreview = ref(false);
const retrying = ref(false);
const cancellingNegotiation = ref(false);
const pausingTransfer = ref(false);
const isLoadingNext = ref(false);
const pollIntervals = ref({ negotiation: null, transfer: null });
const showCatalogsModal = ref(false);
const storePolicies = ref([]);
const storeAssets = ref([]);
const storeOffers = ref([]);
const id = ref("");

// Other existing state
const showAssetWizard = ref(false);
const wizardStep = ref(1);
const isRegistering = ref(false);
const newAsset = ref({ name: "", description: "", type: "", endpoint: "" });
const showPublishModal = ref(false);
const selectedAsset = ref(null);
const publishMethod = ref("default");
const selectedDefaultPolicy = ref("");
const isPublishing = ref(false);
const publishStep = ref("select");
const showPreview = ref(false);
const isSidebarCollapsed = ref(false);
const isMobileSidebarOpen = ref(false);
const selectedCatalogsCount = computed(() => selectedCatalogs.value.length);
const isMobile = ref(false);
const globalCatalogs = ref([]);
const dlmGlobalCatalogs = ref([]);
const globalCatalogDefaults = ref({});
const globalCatalogSaveStatus = ref({});
const catalogSnapshot = ref(null);
const catalogSnapshotLoading = ref(false);
const customPolicy = ref({
  id: "",
  action: "use",
  constraintType: "single",
  constraints: [{ leftOperand: "", operator: "eq", rightOperand: "" }],
});
const localCatalog = {
  id: "local-catalog",
  name: "Local Catalog",
  description: "Your organization's internal data catalog",
  endpoint: "http://localhost:48086",
  protocol: "EDC",
  type: "local",
  status: "connected",
  selected: true,
};
const selectedCatalogs = computed(() =>
  availableCatalogs.value.filter((catalog) => catalog.selected)
);
const selectedGlobalCatalogs = computed(() =>
  selectedCatalogs.value.filter((catalog) => catalog.type === "global")
);
const availableCatalogs = ref([]);
const user = userService.getCookieUser()?.organization[0];

const policyError = ref("");
const registrationStatus = ref({ type: "", message: "" });

const coreMetadataRows = computed(() => {
  const props = selectedAsset.value?.contentData?.properties || {};
  const preferred = [
    "name",
    "description",
    "format",
    "outputPortType",
    "productVersion",
    "payable",
    "tags",
  ];
  const keys = [
    ...preferred.filter((key) => key in props),
    ...Object.keys(props).filter(
      (key) => !preferred.includes(key) && key !== "assetAttributes"
    ),
  ];

  return keys.map((key) => {
    const rawValue = props[key];
    const value =
      typeof rawValue === "object"
        ? JSON.stringify(rawValue)
        : String(rawValue ?? "");
    return {
      key,
      label: formatKey(key),
      value,
    };
  });
});

// Computed properties
const selectedPolicy = computed(() => {
  if (Array.isArray(currentAsset.value.policy)) {
    return currentAsset.value.policy[selectedPolicyIndex.value];
  }

  return currentAsset.value.policy;
});

const hasMultiplePolicies = computed(() => {
  return (
    currentAsset.value?.policy &&
    Array.isArray(currentAsset.value.policy) &&
    currentAsset.value.policy.length > 1
  );
});

const hasAllCredentials = computed(() => {
  if (!currentAsset.value?.required_credentials) return true;
  return currentAsset.value.required_credentials.every((cred) =>
    hasCredential(cred)
  );
});

const acquitionCred = ref(false);

const checkAcquitionCredentials = async (asset) => {
  if (!asset?.name) {
    acquitionCred.value = false;
    return;
  }

  console.log("These are the assets: ", asset);
  acquitionCred.value = true;
  // const response = await axios.post(
  //   `${API_BASE_URL}/repository/find/prop/${user}`,
  //   {
  //     objectType: {
  //       name: "DS2DataOfferPackage",
  //     },
  //     name: asset.name,
  //   },
  //   {
  //     params: {
  //       includeContent: "true",
  //       pageNumber: 1,
  //       pageSize: 100,
  //       sortBy: "_id",
  //     },
  //   }
  // );
  // console.log("Response is: ", response);

  // const source = response.data[0]?.metadata?.source;

  // console.log("Source is: ", source);

  // if (source == "LocalCatalog") {
  //   acquitionCred.value = true;
  // } else {
  //   const credRes = await axios.post(
  //     `${API_BASE_URL}/repository/find/prop/${user}`,
  //     {
  //       objectType: {
  //         name: "DS2Credential",
  //       },
  //       name: asset.name,
  //     },
  //     {
  //       params: {
  //         includeContent: "true",
  //         pageNumber: 1,
  //         pageSize: 100,
  //         sortBy: "_id",
  //       },
  //     }
  //   );
  //   if (credRes.data.length > 0) {
  //     console.log("True");

  //     acquitionCred.value = true;
  //   } else {
  //     console.log("False");

  //     acquitionCred.value = false;
  //   }
  // }
};

watch(
  () => currentAsset.value,
  (asset) => {
    if (asset) checkAcquitionCredentials(asset);
  }
);

const showCustomDestination = computed(() => {
  return transferConfig.value.destinationType === "HttpData";
});

const isPreviousDisabled = computed(() => {
  return contractStatus.value.step === "configuring";
});

const isNextDisabled = computed(() => {
  if (isLoadingNext.value) return true;
  if (contractStatus.value.step === "configuring") {
    return !selectedPolicy;
  }
  return false;
});

const isCloseDisabled = computed(() => {
  return ["negotiating", "transferring", "downloading"].includes(
    contractStatus.value.step
  );
});

const canStartNewTransfer = computed(() => {
  return contractStatus.value.step === "completed";
});

const canReportIssue = computed(() => {
  return contractStatus.value.step === "error";
});

// Helper Functions
const formatEndpoint = (endpoint) => {
  try {
    const url = new URL(endpoint);
    return url.hostname + url.pathname;
  } catch {
    return endpoint.replace(/^https?:\/\//, "").slice(0, 30);
  }
};

const inferInputType = (name, value) => {
  if (typeof value === "boolean") return "boolean";
  if (/^(is|has)[A-Z_]/.test(name)) return "boolean";
  if (typeof value === "number") return "number";
  if (/score|count|total|amount|number|size|price/i.test(name)) return "number";
  return "text";
};

const buildDefaultsFromRecord = (record) => {
  const fields = record?.contentData?.fields || [];
  const defaults = record?.contentData?.defaultValues || {};
  return fields.map((field) => ({
    name: field.name,
    required: !!field.required,
    defaultValue:
      defaults[field.name] !== undefined ? defaults[field.name] : "",
    overrideValue:
      defaults[field.name] !== undefined ? defaults[field.name] : "",
    removed: false,
    inputType: inferInputType(field.name, defaults[field.name]),
  }));
};

const formatFieldValue = (value) => {
  if (value === "" || value === null || value === undefined) return "None";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const toggleFieldRemoval = (field) => {
  if (!field || field.required) return;
  field.removed = !field.removed;
  if (field.removed) {
    field.overrideValue = "";
  }
};

const resolveFieldValue = (field) => {
  const override = field.overrideValue;
  const hasOverride =
    override !== "" && override !== null && override !== undefined;
  const rawValue = hasOverride ? override : field.defaultValue;

  if (field.inputType === "boolean") {
    if (rawValue === true || rawValue === "true") return true;
    if (rawValue === false || rawValue === "false") return false;
    return rawValue;
  }
  if (field.inputType === "number") {
    if (rawValue === "" || rawValue === null || rawValue === undefined)
      return rawValue;
    const asNumber = Number(rawValue);
    return Number.isNaN(asNumber) ? rawValue : asNumber;
  }
  return rawValue;
};

const matchGlobalCatalogRecord = (catalog, records) => {
  if (!Array.isArray(records)) return null;
  const byExternalId = records.find(
    (record) => record.externalId === catalog.externalId
  );
  if (byExternalId) return byExternalId;

  const byEndpoint = records.find(
    (record) => record.externalId === catalog.endpoint
  );
  if (byEndpoint) return byEndpoint;

  const byName = records.find((record) => record.name === catalog.name);
  if (byName) return byName;

  return records[0] || null;
};

const formatKey = (key) =>
  key
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (str) => str.toUpperCase())
    .replace("Odrl", "ODRL")
    .replace("Dct", "DCT");

const formatCredentialType = (type) => {
  if (!type) return "Unknown";
  return type
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (str) => str.toUpperCase())
    .replace("Credential", "");
};

const formatConstraint = (constraint) => {
  if (typeof constraint === "string") return constraint;
  if (
    constraint.leftOperand &&
    constraint.operator &&
    constraint.rightOperand
  ) {
    return `${constraint.leftOperand} ${constraint.operator} ${constraint.rightOperand}`;
  }
  return JSON.stringify(constraint);
};

const getPolicyType = (policy) => {
  if (policy["@type"]) return policy["@type"];
  if (policy["odrl:permission"]) return "ODRL Policy";
  return "Custom Policy";
};

const getConstraints = (policy) => {
  const constraints = [];
  const permission = policy["odrl:permission"];
  if (permission && permission["odrl:constraint"]) {
    const constraint = permission["odrl:constraint"];
    if (Array.isArray(constraint)) {
      constraint.forEach((c) => {
        if (
          c["odrl:leftOperand"] &&
          c["odrl:operator"] &&
          c["odrl:rightOperand"]
        ) {
          constraints.push(
            `${c["odrl:leftOperand"]["@id"] || c["odrl:leftOperand"]} ${
              c["odrl:operator"]
            } ${c["odrl:rightOperand"]}`
          );
        }
      });
    } else if (
      constraint["odrl:leftOperand"] &&
      constraint["odrl:operator"] &&
      constraint["odrl:rightOperand"]
    ) {
      constraints.push(
        `${
          constraint["odrl:leftOperand"]["@id"] ||
          constraint["odrl:leftOperand"]
        } ${constraint["odrl:operator"]} ${constraint["odrl:rightOperand"]}`
      );
    }
  }
  return constraints;
};

// Enhanced Contract Modal Functions
const openContractModal = (asset) => {
  currentAsset.value = asset;

  console.log("Opening contract modal for asset:", asset);

  modalTitle.value = `Request Access: ${asset.name || asset.id}`;
  showContractModal.value = true;
  selectedPolicyIndex.value = 0; // Reset to first policy

  // Reset contract state
  contractStatus.value = {
    step: "configuring",
    subStep: "preparing",
    transferSubStep: "initiating",
    message: "",
    negotiationId: null,
    contractAgreementId: null,
    transferId: null,
    error: null,
    troubleshooting: null,
    startTime: new Date(),
    lastCheck: null,
    completionTime: null,
    state: null,
    policyId: null,
  };

  transferProgress.value = 0;
  transferSpeed.value = null;
  transferSize.value = null;
  transferEstimatedTime.value = null;
  transferPaused.value = false;
  retrievedData.value = null;
  showPolicyPreview.value = false;

  clearPollingIntervals();
};

const selectPolicy = (index) => {
  selectedPolicyIndex.value = index;
};

const getCredentialConstraint = (credType) => {
  if (!currentAsset.value?.credential_constraints) return null;

  // Check if credential_constraints is an object with credential types as keys
  if (
    typeof currentAsset.value.credential_constraints === "object" &&
    !Array.isArray(currentAsset.value.credential_constraints)
  ) {
    return currentAsset.value.credential_constraints[credType];
  }

  // Check if credential_constraints is an object with a DataProcessorCredential property
  if (currentAsset.value.credential_constraints.DataProcessorCredential) {
    return currentAsset.value.credential_constraints.DataProcessorCredential;
  }

  // Check if credential_constraints has a constraint array
  if (Array.isArray(currentAsset.value.credential_constraints.constraint)) {
    const constraint = currentAsset.value.credential_constraints.constraint[0];
    if (
      constraint &&
      constraint.leftOperand &&
      constraint.operator &&
      constraint.rightOperand
    ) {
      return `${constraint.leftOperand} ${constraint.operator} ${constraint.rightOperand}`;
    }
  }

  return null;
};

const closeModal = () => {
  if (isCloseDisabled.value) {
    if (!confirm("Transfer is in progress. Are you sure you want to cancel?")) {
      return;
    }
    cancelTransfer();
  }
  showContractModal.value = false;
  clearPollingIntervals();
};

const clearPollingIntervals = () => {
  if (pollIntervals.value.negotiation) {
    clearInterval(pollIntervals.value.negotiation);
    pollIntervals.value.negotiation = null;
  }
  if (pollIntervals.value.transfer) {
    clearInterval(pollIntervals.value.transfer);
    pollIntervals.value.transfer = null;
  }
};

const formatTime = (date) => {
  if (!date) return "";
  return new Date(date).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

const togglePolicyPreview = () => {
  showPolicyPreview.value = !showPolicyPreview.value;
};

const previousStep = () => {
  if (contractStatus.value.step === "negotiating") {
    contractStatus.value.step = "configuring";
  } else if (contractStatus.value.step === "transferring") {
    contractStatus.value.step = "negotiating";
  }
};

const nextStep = async () => {
  isLoadingNext.value = true;

  try {
    if (contractStatus.value.step === "configuring") {
      await startContractNegotiation();
    } else if (contractStatus.value.step === "negotiating") {
      await startTransfer();
    }
  } catch (error) {
    console.error("Error in next step:", error);
    contractStatus.value.step = "error";
    contractStatus.value.message = error.message || "An error occurred";
    contractStatus.value.error = error.toString();
  } finally {
    isLoadingNext.value = false;
  }
};

const getNextButtonText = () => {
  switch (contractStatus.value.step) {
    case "configuring":
      return acquitionCred.value
        ? "Start Contract Negotiation"
        : "Missing Credentials";
    case "negotiating":
      return "Initiate Data Transfer";
    default:
      return "Next";
  }
};

const getLoadingText = () => {
  switch (contractStatus.value.step) {
    case "configuring":
      return "Starting negotiation...";
    case "negotiating":
      return "Starting transfer...";
    default:
      return "Processing...";
  }
};

const startContractNegotiation = async () => {
  contractStatus.value.step = "negotiating";
  contractStatus.value.subStep = "preparing";

  // Use the selected policy
  const selectedPolicyObj = selectedPolicy.value;

  console.log("This is the selected policy value: ", selectedPolicyObj);

  // if (selectedPolicyObj && selectedPolicyObj["@id"]) {
  //   policyId = selectedPolicyObj["@id"];
  // }

  contractStatus.value.policyId = selectedPolicyObj["@id"];

  // Simulate preparation
  await simulateDelay(1000);
  contractStatus.value.subStep = "sending";

  console.log("Current Asset is: ", currentAsset.value);

  try {
    // Send contract request using existing backend API
    const response = await axios.post(`${API_BASE_URL}/contract`, {
      asset_id: currentAsset.value?.id,
      end_point: currentAsset.value?.endpoint,
      did: currentAsset.value?.did,
      policy: selectedPolicyObj,
      catalog_name: currentAsset.value?.catalog_name,
    });

    console.log("Response from contract initiation: ", response);

    if (response.data.success) {
      contractStatus.value.negotiationId = response.data.negotiation_id;
      contractStatus.value.contractAgreementId =
        response.data.contract_agreement_id;
      contractStatus.value.subStep = "waiting";

      // Start polling for negotiation status
      await pollNegotiationStatus(
        response.data.contract_agreement_id,
        currentAsset.value?.id,
        currentAsset.value?.endpoint,
        extractProviderFromEndpoint(currentAsset.value?.endpoint),
        currentAsset.value?.did,
        response.data
      );
    } else {
      throw new Error(
        response.data.error || "Failed to start contract negotiation"
      );
    }
  } catch (error) {
    contractStatus.value.step = "error";
    contractStatus.value.message = "Failed to establish contract";
    contractStatus.value.error = error.toString();
  }
};

const extractProviderFromEndpoint = (endpoint) => {
  try {
    const url = new URL(endpoint);
    return url.hostname.replace("-controlplane", "");
  } catch {
    return "provider";
  }
};

const pollNegotiationStatus = async (
  contractAgreementId,
  assetId,
  endpoint,
  provider,
  did,
  response
) => {
  contractStatus.value.lastCheck = new Date();

  try {
    // Use the existing polling logic from the backend
    const hostname = new URL(endpoint).hostname;
    const providerName = hostname.replace("-controlplane", "");

    // Start the actual contract establishment
    await establishContract(response);
    console.log("Response from contract initiation: ", response);
  } catch (error) {
    contractStatus.value.step = "error";
    contractStatus.value.message = "Failed to complete contract negotiation";
    contractStatus.value.error = error.toString();
  }
};

const startTransfer = async () => {
  contractStatus.value.step = "transferring";
  contractStatus.value.transferSubStep = "initiating";

  try {
    // Prepare transfer request based on configuration
    const transferRequest = {
      "@context": {
        "@vocab": "https://w3id.org/edc/v0.0.1/ns/",
        edc: "https://w3id.org/edc/v0.0.1/ns/",
        tx: "https://w3id.org/tractusx/v0.0.1/ns/",
      },
      "@type": "TransferRequest",
      assetId: currentAsset.value?.id,
      contractId: contractStatus.value.contractAgreementId,
      connectorId: currentAsset.value?.did,
      connectorAddress: `${currentAsset.value?.endpoint}`,
      protocol: transferConfig.value.protocol,
      dataDestination: {
        "@type": "DataAddress",
        type: transferConfig.value.destinationType,
      },
    };

    // Add custom endpoint if configured
    if (transferConfig.value.customEndpoint) {
      transferRequest.dataDestination.baseUrl =
        transferConfig.value.customEndpoint;
    }

    const response = await axios.post(
      `${API_BASE_URL}/contract/transfer`,
      transferRequest
    );

    if (response.data.success) {
      contractStatus.value.transferId = response.data.transfer_id;
      contractStatus.value.transferSubStep = "preparing";

      // Simulate preparation
      await simulateDelay(1000);
      contractStatus.value.transferSubStep = "fetching";

      // Start transfer progress simulation
      simulateTransferProgress();

      // Poll transfer status using existing backend
      const provider = extractProviderFromEndpoint(
        currentAsset.value?.endpoint
      );
      pollTransferStatus(response.data.transfer_id, provider);
    } else {
      throw new Error(response.data.error || "Failed to initiate transfer");
    }
  } catch (error) {
    contractStatus.value.step = "error";
    contractStatus.value.message = "Failed to initiate data transfer";
    contractStatus.value.error = error.toString();
  }
};

const simulateTransferProgress = () => {
  let progress = 0;
  const interval = setInterval(() => {
    if (transferPaused.value) return;

    progress += Math.random() * 5;
    if (progress > 100) progress = 100;

    transferProgress.value = Math.min(progress, 100);

    // Simulate transfer stats
    if (progress < 100) {
      transferSpeed.value = `${(Math.random() * 5 + 1).toFixed(1)} MB/s`;
      transferSize.value = `${(Math.random() * 100 + 50).toFixed(0)} MB`;
      transferEstimatedTime.value = `${Math.max(
        0,
        Math.floor((100 - progress) / 5)
      )}s`;
    }

    if (progress >= 100) {
      clearInterval(interval);
      contractStatus.value.transferSubStep = "receiving";
      // Simulate processing
      setTimeout(() => {
        contractStatus.value.step = "completed";
        contractStatus.value.completionTime = new Date();
        // Fetch actual data
        //fetchTransferData();
      }, 1500);
    }
  }, 300);
};

const pollTransferStatus = (transferId, provider) => {
  if (pollIntervals.value.transfer) clearInterval(pollIntervals.value.transfer);
  pollIntervals.value.transfer = setInterval(async () => {
    try {
      await getTransferredData(transferId, provider);
    } catch (error) {
      console.error("Error polling transfer status:", error);
      clearInterval(pollIntervals.value.transfer);
      pollIntervals.value.transfer = null;
      contractStatus.value.step = "error";
      contractStatus.value.message =
        error.response?.data?.detail ||
        error.message ||
        "Error checking transfer status";
    }
  }, 2000);
};

const fetchTransferData = async () => {
  try {
    console.log("State before fetching data: ", contractStatus.value.state);

    const provider = extractProviderFromEndpoint(currentAsset.value?.endpoint);
    if (contractStatus.value.state == "COMPLETED") {
      throw {
        response: {
          data: {
            detail: "The transfer has been completed.",
          },
        },
      };
    }
    const response = await axios.get(
      `${API_BASE_URL}/contract/data/${contractStatus.value.transferId}/${provider}`
    );
    retrievedData.value = response.data;
    toast.add({
      severity: "success",
      summary: "Info",
      detail: "Transfer data fetched successfully",
      life: 3000,
    });
  } catch (error) {
    console.error("Error fetching transfer data:", error);
    toast.add({
      severity: "error",
      summary: "Error Message",
      detail: error.response.data.detail,
      life: 3000,
    });
    retrievedData.value = { error: "Could not fetch data preview" };
  }
};

const cancelNegotiation = () => {
  cancellingNegotiation.value = true;
  setTimeout(() => {
    contractStatus.value.step = "configuring";
    contractStatus.value.subStep = "preparing";
    cancellingNegotiation.value = false;
  }, 1000);
};

const pauseTransfer = () => {
  if (transferPaused.value) {
    transferPaused.value = false;
  } else {
    pausingTransfer.value = true;
    setTimeout(() => {
      transferPaused.value = true;
      pausingTransfer.value = false;
    }, 500);
  }
};

const cancelTransfer = () => {
  contractStatus.value.step = "configuring";
  contractStatus.value.subStep = "preparing";
  transferPaused.value = false;
};

const calculateDuration = () => {
  if (!contractStatus.value.startTime || !contractStatus.value.completionTime) {
    return "N/A";
  }

  const start = new Date(contractStatus.value.startTime);
  const end = new Date(contractStatus.value.completionTime);
  const duration = end - start;

  const minutes = Math.floor(duration / 60000);
  const seconds = Math.floor((duration % 60000) / 1000);

  return `${minutes}m ${seconds}s`;
};

const getDataRowCount = () => {
  if (!retrievedData.value) return 0;
  if (Array.isArray(retrievedData.value)) {
    return retrievedData.value.length;
  }
  return Object.keys(retrievedData.value).length;
};

const getDataColumnCount = () => {
  if (!retrievedData.value) return 0;
  if (Array.isArray(retrievedData.value) && retrievedData.value.length > 0) {
    return Object.keys(retrievedData.value[0]).length;
  }
  return Object.keys(retrievedData.value).length;
};

const downloadData = () => {
  if (!retrievedData.value) return;

  const dataStr = JSON.stringify(retrievedData.value, null, 2);
  const dataBlob = new Blob([dataStr], { type: "application/json" });
  const url = URL.createObjectURL(dataBlob);

  const link = document.createElement("a");
  link.href = url;
  link.download = `data_${currentAsset.value?.id}_${
    new Date().toISOString().split("T")[0]
  }.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const copyDataToClipboard = async () => {
  if (!retrievedData.value) return;

  try {
    const dataStr = JSON.stringify(retrievedData.value, null, 2);
    await navigator.clipboard.writeText(dataStr);
    alert("Data copied to clipboard!");
  } catch (error) {
    console.error("Failed to copy:", error);
    alert("Failed to copy data to clipboard");
  }
};

const changeState = async () => {
  try {
    const response = await axios.post(`${API_BASE_URL}/transfer/completed`, {
      transfer_id: contractStatus.value?.transferId,
      contract_id: contractStatus.value?.contractAgreementId,
    });

    console.log("Response from starting new transfer: ", response.status);
    contractStatus.value.state = "COMPLETED";
    toast.add({
      severity: "success",
      summary: "Info",
      detail: "Transfer Completed",
      life: 3000,
    });
  } catch (error) {
    console.error("Error starting new transfer:", error.response.data.detail);
    toast.add({
      severity: "danger",
      summary: "Info",
      detail: error.response.data.detail,
      life: 3000,
    });
  }
};

const viewContractDetails = () => {
  alert(
    `Contract Agreement ID: ${contractStatus.value.contractAgreementId}\nNegotiation ID: ${contractStatus.value.negotiationId}`
  );
};

const retryTransfer = () => {
  retrying.value = true;
  contractStatus.value = {
    step: "configuring",
    subStep: "preparing",
    transferSubStep: "initiating",
    message: "",
    negotiationId: null,
    contractAgreementId: null,
    transferId: null,
    error: null,
    troubleshooting: null,
    startTime: new Date(),
    lastCheck: null,
    completionTime: null,
    state: null,
    policyId: null,
  };
  transferProgress.value = 0;
  transferSpeed.value = null;
  transferSize.value = null;
  transferEstimatedTime.value = null;
  transferPaused.value = false;

  setTimeout(() => {
    retrying.value = false;
  }, 1000);
};

const reportIssue = () => {
  const issueDetails = {
    assetId: currentAsset.value?.id,
    error: contractStatus.value.error,
    negotiationId: contractStatus.value.negotiationId,
    transferId: contractStatus.value.transferId,
    timestamp: new Date().toISOString(),
  };

  console.log("Reporting issue:", issueDetails);
  alert("Issue reported. Our team will investigate.");
};

const simulateDelay = (ms) => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

// Existing backend integration functions
const establishContract = async (response) => {
  try {
    loading.value.contract = true;
    contractStatus.value.step = "negotiating";
    contractStatus.value.message = "Contract negotiation in progress...";

    // const res = await axios.post(`${API_BASE_URL}/contract`, {
    //   asset_id: assetId,
    //   end_point: endpoint,
    //   did: did,
    //   policy: selectedPolicy.value, // Include selected policy
    // });

    console.log("Response from contract initiation: ", response);

    if (response.success) {
      contractStatus.value.negotiationId = response.negotiation_id;
      contractStatus.value.contractAgreementId = response.contract_agreement_id;
      contractStatus.value.subStep = "verified";
      contractStatus.value.state = "FINALIZED";
      contractStatus.value.message =
        "Contract negotiation completed successfully";
    } else {
      contractStatus.value.step = "error";
      contractStatus.value.message =
        response.error || "Failed to establish contract";
    }
  } catch (error) {
    console.error("Error establishing contract:", error);
    contractStatus.value.step = "error";
    contractStatus.value.message =
      error.response?.data?.detail ||
      error.message ||
      "An unknown error occurred";
  } finally {
    loading.value.contract = false;
  }
};

const getTransferredData = async (transferId, provider) => {
  try {
    const dataRes = await axios.get(
      `${API_BASE_URL}/contract/data/${transferId}/${provider}`
    );
    retrievedData.value = dataRes.data;
    contractStatus.value.step = "completed";
    contractStatus.value.message = "Data transfer completed successfully";
    clearInterval(pollIntervals.value.transfer);
  } catch (error) {
    console.error("Error getting transferred data:", error);
    contractStatus.value.step = "error";
    contractStatus.value.message =
      error.response?.data?.detail || error.message || "Error retrieving data";
  }
};

// CRITICAL FIX: Updated credential check to match your data structure
const hasCredential = (credentialType, asset) => {
  console.log("this is the selected asset: ", asset);
  console.log("This is the credentials: ", credentialType);

  if (!credentialType || !credentials.value || credentials.value.length === 0) {
    console.log("No credentials or credential type");
    return false;
  }

  // Check credentials array
  const hasCred = credentials.value.some((cred) => {
    // Check if credential matches the type
    if (cred.type === credentialType) {
      console.log("Found matching credential by type:", cred.type);
      return true;
    }

    // Check if credential has a type array
    if (Array.isArray(cred.type) && cred.type.includes(credentialType)) {
      console.log("Found matching credential in type array");
      return true;
    }

    return false;
  });

  return hasCred;
};

const getStoreId = (value) =>
  value?.externalId || value?.contentData?.["@id"] || value?.id;
const getStoreName = (value) => value?.name || value?.contentData?.name;
const getConnectorId = (value) => value?.["@id"] || value?.value?.["@id"];
const getConnectorName = (value) => value?.name;

const findConnectorForStore = (value, type) => {
  if (type == "Asset") {
    const id = getStoreId(value);
    if (id && AssetconnectorById.value.has(id))
      return AssetconnectorById.value.get(id);
    const name = getStoreName(value);
    if (name && AssetconnectorByName.value.has(name))
      return AssetconnectorByName.value.get(name);
    return null;
  } else {
    const id = getStoreId(value);
    if (id && connectorById.value.has(id)) return connectorById.value.get(id);
    const name = getStoreName(value);
    if (name && connectorByName.value.has(name))
      return connectorByName.value.get(name);
    return null;
  }
};

const findStoreForConnector = (value, type) => {
  if (type == "Asset") {
    const id = getConnectorId(value);
    if (id && AssetstoreById.value.has(id)) return AssetstoreById.value.get(id);
    const name = getConnectorName(value);
    if (name && AssetstoreByName.value.has(name))
      return AssetstoreByName.value.get(name);
    return null;
  } else {
    const id = getConnectorId(value);
    if (id && storeById.value.has(id)) return storeById.value.get(id);
    const name = getConnectorName(value);
    if (name && storeByName.value.has(name)) return storeByName.value.get(name);
    return null;
  }
};

const storeById = computed(() => {
  const map = new Map();
  storePolicies.value.forEach((policy) => {
    const key = getStoreId(policy);
    if (key) map.set(key, policy);
  });
  return map;
});

const AssetstoreById = computed(() => {
  const map = new Map();
  storeAssets.value.forEach((policy) => {
    const key = getStoreId(policy);
    if (key) map.set(key, policy);
  });
  return map;
});

const storeByName = computed(() => {
  const map = new Map();
  storePolicies.value.forEach((asset) => {
    const key = getStoreName(asset);
    if (key) map.set(key, asset);
  });
  return map;
});

const AssetstoreByName = computed(() => {
  const map = new Map();
  storeAssets.value.forEach((asset) => {
    const key = getStoreName(asset);
    if (key) map.set(key, asset);
  });
  return map;
});

const connectorById = computed(() => {
  const map = new Map();
  policies.value.forEach((policy) => {
    const key = getConnectorId(policy);
    if (key) map.set(key, policy);
  });
  return map;
});

const AssetconnectorById = computed(() => {
  const map = new Map();
  providerAssets.value.forEach((policy) => {
    const key = getConnectorId(policy);
    if (key) map.set(key, policy);
  });
  return map;
});

const connectorByName = computed(() => {
  const map = new Map();
  policies.value.forEach((policy) => {
    const key = getConnectorName(policy);
    if (key) map.set(key, policy);
  });
  return map;
});

const AssetconnectorByName = computed(() => {
  const map = new Map();
  providerAssets.value.forEach((policy) => {
    const key = getConnectorName(policy);
    if (key) map.set(key, policy);
  });
  return map;
});

const mergedPolicies = computed(() => {
  const map = new Map();
  const matchedConnector = new Set();
  storePolicies.value.forEach((policy) => {
    const key = getStoreId(policy) || getStoreName(policy);
    if (!key) return;
    const connector = findConnectorForStore(policy, "Policy");
    if (connector) {
      const cKey = getConnectorId(connector) || getConnectorName(connector);
      if (cKey) matchedConnector.add(cKey);
    }
    map.set(key, { key, store: policy, connector });
  });
  policies.value.forEach((policy) => {
    const key = getConnectorId(policy) || getConnectorName(policy);
    if (!key) return;
    if (matchedConnector.has(key)) return;
    const storeMatch = findStoreForConnector(policy, "Policy");
    if (storeMatch) return;
    map.set(key, { key, store: null, connector: policy });
  });
  return Array.from(map.values());
});

const mergedAssets = computed(() => {
  const map = new Map();
  const matchedConnector = new Set();
  storeAssets.value.forEach((asset) => {
    const key = getStoreId(asset) || getStoreName(asset);
    if (!key) return;
    const connector = findConnectorForStore(asset, "Asset");
    if (connector) {
      const cKey = getConnectorId(connector) || getConnectorName(connector);
      if (cKey) matchedConnector.add(cKey);
    }
    map.set(key, {
      key,
      store: asset,
      connector,
    });
  });
  providerAssets.value.forEach((asset) => {
    const key = getConnectorId(asset) || getConnectorName(asset);
    if (!key) return;
    if (matchedConnector.has(key)) return;
    const storeMatch = findStoreForConnector(asset, "Asset");
    if (storeMatch) return;
    map.set(key, { key, store: null, connector: asset });
  });
  return Array.from(map.values());
});

const isMissingCredential = (asset, credentialType) =>
  !!(
    asset &&
    credentialType &&
    asset.missing_credentials &&
    asset.missing_credentials.includes(credentialType)
  );

// Existing methods
const toggleRequirements = (assetId) => {
  console.log("Here", assetId);
  assetShowReq.value = assetShowReq.value === assetId ? null : assetId;
};

const assetForm = (item = null, objectId = null) => {
  console.log("Item is: ", item);
  console.log("Object is: ", objectId);

  if (item && objectId) {
    id.value = objectId.id || "";

    existingAsset.value = item;
  } else {
    existingAsset.value = null;
  }
  assetViewOnly.value = false;
  switchView("asset");
};

const viewAssetForm = (item, objectId) => {
  if (!item || !objectId) return;

  id.value = objectId.id || "";
  existingAsset.value = item;
  assetViewOnly.value = true;
  switchView("asset");
};

const switchView = async (view) => {
  currentView.value = view;
  if (view === "subscriber") {
    loadParticipantData();
  } else if (view === "global-catalogs") {
    await loadGlobalCatalogs();
  } else {
    await loadProviderAssets();
  }
};

const openProviderAssetsConnector = () => {
  providerAssetsSourceOverride.value = "connector";
  switchView("provider-assets");
};

const openProviderAssetsStore = () => {
  providerAssetsSourceOverride.value = "store";
  switchView("provider-assets");
};

const openProviderPoliciesConnector = () => {
  providerPoliciesSourceOverride.value = "connector";
  switchView("provider-policies");
};

const openProviderPoliciesStore = () => {
  providerPoliciesSourceOverride.value = "store";
  switchView("provider-policies");
};

const openProviderOfferConnector = () => {
  providerOfferSourceOverride.value = "connector";
  switchView("provider-offers");
};

const openProviderOfferStore = () => {
  providerOfferSourceOverride.value = "store";
  switchView("provider-offers");
};

const openPublishModal = (offer) => {
  console.log("Opening publish modal for offer:", offer);

  openCatalogsModal(offer);
};

const loadProviderAssets = async () => {
  try {
    loading.value.assets = true;
    const response = await axios.get(`${API_BASE_URL}/provider/assets`);
    providerAssets.value = response.data;
    console.log("Fetched provider assets:", providerAssets.value);
  } catch (error) {
    console.error("Error fetching provider assets:", error);
    alert("Failed to load provider assets.");
  } finally {
    loading.value.assets = false;
  }
};

const handleAssetCreated = () => {
  loadAssets();
  loadProviderAssets();

  switchView("provider-assets");
};

const loadPolicies = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/policies`);
    policies.value = response.data;
    console.log("These are the policies: ", policies.value);
  } catch (error) {
    console.error("Error fetching policies:", error);
  }
};

const loadStorePolicies = async () => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/repository/find/prop/${user}`,
      {
        objectType: {
          name: "DataOffer-Policy",
        },
      },
      {
        params: {
          includeContent: "true",
          pageNumber: 1,
          pageSize: 100,
          sortBy: "_id",
        },
      }
    );
    // Assuming the API returns an array of policies
    //policies.value = response.data;
    console.log("Full response from policies endpoint:", response.data);

    storePolicies.value = response.data;
    console.log("Policies loaded from API:", storePolicies.value);
    dlmStoreStatus.value = {
      state: "online",
      message: "Online",
      lastChecked: new Date(),
    };
  } catch (error) {
    console.error("Error loading policies:", error);
    toast.add({
      severity: "error",
      summary: "Error Message",
      detail: error?.response.data?.detail,
      life: 3000,
    });
    dlmStoreStatus.value = {
      state: "offline",
      message: "Offline",
      lastChecked: new Date(),
    };
  }
};

const loadConnectorInfo = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/connectorInfo`);
    contractInfo.value = response.data;
    console.log("Connector Info:", response.data);
  } catch (error) {
    console.error("Error fetching connector info:", error);
  }
};

const buildAvailableCatalogs = () => {
  const globalEntries = globalCatalogs.value.map((catalog) => ({
    id: `global-${catalog.category}-${catalog.endpoint}`,
    name: catalog.name,
    description:
      catalog.description ||
      (catalog.category === "marketplace"
        ? "Global marketplace destination"
        : "Global catalog destination"),
    endpoint: catalog.endpoint,
    protocol: catalog.protocol || "EDC",
    type: "global",
    status: "connected",
    selected: false,
    category: catalog.category,
  }));

  availableCatalogs.value = [
    { ...localCatalog, selected: true },
    ...globalEntries,
  ];
};

const saveTimers = ref(new Map());

const ensureGlobalDefaultsBucket = (catalog) => {
  if (globalCatalogDefaults.value[catalog.id]) return;
  const record = matchGlobalCatalogRecord(catalog, dlmGlobalCatalogs.value);
  globalCatalogDefaults.value = {
    ...globalCatalogDefaults.value,
    [catalog.id]: {
      record,
      fields: record ? buildDefaultsFromRecord(record) : [],
    },
  };
  globalCatalogSaveStatus.value = {
    ...globalCatalogSaveStatus.value,
    [catalog.id]: record ? "Saved" : "Missing DLM record",
  };
};

const loadDlmGlobalCatalogs = async () => {
  if (!user) return;
  try {
    const response = await axios.post(
      `${API_BASE_URL}/repository/find/prop/${user}`,
      { objectType: { name: "globalCatalog" } },
      {
        params: {
          includeContent: "true",
          pageNumber: 1,
          pageSize: 200,
          sortBy: "_id",
        },
      }
    );

    const raw = response.data;
    dlmGlobalCatalogs.value = Array.isArray(raw) ? raw : raw ? [raw] : [];

    const updatedDefaults = {};
    const updatedStatus = {};

    availableCatalogs.value
      .filter((catalog) => catalog.type === "global")
      .forEach((catalog) => {
        const record = matchGlobalCatalogRecord(
          catalog,
          dlmGlobalCatalogs.value
        );
        updatedDefaults[catalog.id] = {
          record,
          fields: record ? buildDefaultsFromRecord(record) : [],
        };
        updatedStatus[catalog.id] = record ? "Saved" : "Missing DLM record";
      });

    globalCatalogDefaults.value = updatedDefaults;
    globalCatalogSaveStatus.value = updatedStatus;
  } catch (error) {
    console.error("Error loading global catalog defaults:", error);
  }
};

const queueSaveGlobalDefaults = (catalog) => {
  if (!catalog) return;
  globalCatalogSaveStatus.value = {
    ...globalCatalogSaveStatus.value,
    [catalog.id]: "Saving...",
  };
  const timers = saveTimers.value;
  if (timers.get(catalog.id)) {
    clearTimeout(timers.get(catalog.id));
  }
  const timer = setTimeout(() => saveGlobalDefaults(catalog), 500);
  timers.set(catalog.id, timer);
};

const saveGlobalDefaults = async (catalog) => {
  const bucket = globalCatalogDefaults.value[catalog.id];
  if (!bucket?.record) {
    globalCatalogSaveStatus.value = {
      ...globalCatalogSaveStatus.value,
      [catalog.id]: "Missing DLM record",
    };
    return;
  }

  const defaultValues = {};
  bucket.fields.forEach((field) => {
    defaultValues[field.name] = field.value;
  });

  const updatedRecord = {
    ...bucket.record,
    contentData: {
      ...(bucket.record.contentData || {}),
      fields: bucket.fields.map((field) => ({
        name: field.name,
        required: field.required,
      })),
      defaultValues,
      generatedAt: new Date().toISOString(),
    },
  };

  try {
    await axios.post(`${API_BASE_URL}/repository/update/full`, updatedRecord);
    globalCatalogDefaults.value = {
      ...globalCatalogDefaults.value,
      [catalog.id]: {
        ...bucket,
        record: updatedRecord,
      },
    };
    globalCatalogSaveStatus.value = {
      ...globalCatalogSaveStatus.value,
      [catalog.id]: "Saved",
    };
  } catch (error) {
    console.error("Error saving catalog defaults:", error);
    globalCatalogSaveStatus.value = {
      ...globalCatalogSaveStatus.value,
      [catalog.id]: "Save failed",
    };
  }
};

const loadGlobalCatalogs = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/users/${user}/catalogs`);
    const data = response.data;
    globalCatalogs.value = Array.isArray(data) ? data : data ? [data] : [];
    buildAvailableCatalogs();
  } catch (error) {
    console.error("Error fetching global catalogs:", error);
    globalCatalogs.value = [];
  }
};

const openCatalogsModal = async (offer) => {
  buildAvailableCatalogs();
  globalCatalogDefaults.value = {};
  globalCatalogSaveStatus.value = {};
  publishStep.value = "select";

  membershipPolicy.value = storePolicies.value.find((policy) => {
    return policy.externalId == offer.contentData.accessPolicyId;
  });

  if (!membershipPolicy.value) {
    toast.add({
      severity: "error",
      summary: "Error Message",
      detail: "No Policy found! Cannot publish this data offer.",
      life: 3000,
    });
    return;
  }

  selectedOffer.value = offer;

  storeSelectedPolicy.value = storePolicies.value.find((policy) => {
    return (
      policy.externalId == selectedOffer.value.contentData.contractPolicyId
    );
  });

  selectedAsset.value = storeAssets.value.find(
    (asset) =>
      asset?.contentData["@id"] ==
      selectedOffer.value.contentData.assetsSelector.operandRight
  );

  console.log("Selected asset for publishing: ", selectedAsset.value);
  console.log("Selected offer for publishing: ", selectedOffer.value);
  console.log("policy for publishing: ", storeSelectedPolicy.value);
  console.log("Membership for publishing: ", membershipPolicy.value);

  await loadDlmGlobalCatalogs();
  catalogSnapshot.value = null;

  showCatalogsModal.value = true;
};

const closeCatalogsModal = () => {
  showCatalogsModal.value = false;
  publishStep.value = "select";
};

const toggleCatalogSelection = async (catalogId) => {
  const catalog = availableCatalogs.value.find((c) => c.id === catalogId);
  if (catalog) {
    if (catalog.type === "local") {
      catalog.selected = true;
      return;
    }
    catalog.selected = !catalog.selected;

    if (catalog.type === "global") {
      if (catalog.selected) {
        ensureGlobalDefaultsBucket(catalog);
        if (!catalogSnapshot.value && !catalogSnapshotLoading.value) {
          //await ensureCatalogSnapshot();
        }
      } else {
        const updatedDefaults = { ...globalCatalogDefaults.value };
        delete updatedDefaults[catalog.id];
        globalCatalogDefaults.value = updatedDefaults;
      }
    }
  }
};

const ensureCatalogSnapshot = async () => {
  if (catalogSnapshot.value || catalogSnapshotLoading.value) return;
  catalogSnapshotLoading.value = true;
  try {
    const response = await axios.get(`${API_BASE_URL}/requestCatalog`);
    console.log("Catalog snapshot response:", response.data);

    catalogSnapshot.value = response.data;
  } catch (error) {
    console.error("Error fetching catalog snapshot:", error);
  } finally {
    catalogSnapshotLoading.value = false;
  }
};

const buildSelectedGlobalMetadata = () =>
  selectedGlobalCatalogs.value.map((catalog) => ({
    "ds2:fields": (globalCatalogDefaults.value[catalog.id]?.fields || [])
      .filter((field) => !(field.removed && !field.required))
      .map((field) => ({
        "ds2:name": field.name,
        "ds2:required": field.required,
        "ds2:value": resolveFieldValue(field),
      })),
  }));

const buildGlobalCatalogPayload = (snapshot) => {
  const assetId =
    selectedAsset.value?.contentData?.["@id"] ||
    selectedAsset.value?.["@id"] ||
    selectedAsset.value?.contentData?.id;
  if (!snapshot || !assetId) return null;

  let filteredDataset = [];
  const datasetSource = snapshot["dcat:dataset"];

  if (Array.isArray(datasetSource)) {
    filteredDataset = datasetSource.filter(
      (dataset) => dataset["@id"] === assetId
    );
  } else if (datasetSource && datasetSource["@id"] === assetId) {
    filteredDataset = [datasetSource];
  }

  // filteredDataset.forEach((dataset) => {
  //   dataset["title"] = dataset["name"];
  //   delete dataset["name"];
  //   delete dataset["published"];
  //   dataset["keyword"] = ["test"];
  //   dataset["theme"] = ["Intersectional"];
  // });

  return {
    payload: {
      "@type": "dcat:Catalog",
      "dspace:participantId": snapshot["dspace:participantId"],
      "dcat:dataset": [filteredDataset[0]],
      "dcat:service": snapshot["dcat:service"] || [],
    },
  };
};

const globalCatalogPayloadPreview = computed(() => {
  if (selectedGlobalCatalogs.value.length === 0) return {};
  const snapshot = catalogSnapshot.value;
  if (!snapshot) {
    return {
      note: "Catalog snapshot not loaded yet.",
      coreMetadata: Object.fromEntries(
        coreMetadataRows.value.map((row) => [row.key, row.value])
      ),
      globalCatalogMetadata: buildSelectedGlobalMetadata(),
    };
  }
  return buildGlobalCatalogPayload(snapshot);
});

const publishToCatalogs = async () => {
  if (selectedCatalogsCount.value === 0) {
    alert("Please select at least one catalog to publish to.");
    return;
  }

  isPublishing.value = true;

  try {
    const selectedGlobalCatalogs = selectedCatalogs.value.filter(
      (c) => c.type === "global"
    );

    console.log(
      "store policy before publishing: ",
      storeSelectedPolicy.value.contentData
    );
    console.log(
      "membership policy before publishing: ",
      membershipPolicy.value.contentData
    );

    console.log("Merged policies: ", mergedPolicies.value);

    const policyToPublish = mergedPolicies.value.find(
      (p) => p.key == storeSelectedPolicy.value.externalId
    );

    const membershipPolicyToPublish = mergedPolicies.value.find(
      (p) => p.key == membershipPolicy.value.externalId
    );

    const assetsToPublish = mergedAssets.value.find(
      (p) => p.key == selectedAsset.value.externalId
    );

    console.log(
      "The policy to publish in the connector: ",
      policyToPublish.store.contentData
    );

    let edcPolicy = null;

    if (isEdcPolicy(policyToPublish.store.contentData)) {
      edcPolicy = policyToPublish.store.contentData;
    } else {
      edcPolicy = convertToEdcPolicy(policyToPublish.store.contentData);
    }

    assetsToPublish.store.contentData.properties["ds2:PolicyID"] =
      policyToPublish.store.id;

    console.log("converted EDC policy: ", edcPolicy);

    console.log("Selected asset is: ", selectedAsset.value.contentData);
    console.log("Policy to publish: ", JSON.stringify(edcPolicy, null, 2));

    if (!policyToPublish.connector) {
      const storeRes = await axios.post(`${API_BASE_URL}/policy/publish`, {
        custom_policy: edcPolicy,
      });

      console.log("Response from store policy publish: ", storeRes.data);
    }

    if (
      !membershipPolicyToPublish.connector &&
      storeSelectedPolicy.value.name !== membershipPolicyToPublish.store.name
    ) {
      await axios.post(`${API_BASE_URL}/policy/publish`, {
        custom_policy: membershipPolicyToPublish.store.contentData,
      });
    }

    console.log(
      "Selected asset before publishing: ",
      JSON.stringify(selectedAsset.value.contentData, null, 2)
    );
    console.log(
      "Selected offer before publishing: ",
      selectedOffer.value.contentData
    );

    if (!assetsToPublish.connector) {
      const assetRes = await axios.post(`${API_BASE_URL}/assets/publish`, {
        asset: assetsToPublish.store.contentData,
      });
      if (!assetRes.data.success) {
        throw new Error(assetRes.data.error || "Failed to publish asset");
      }
    } else {
      const assetRes = await axios.post(`${API_BASE_URL}/assets/edit`, {
        asset: assetsToPublish.store.contentData,
      });
      if (!assetRes.data.success) {
        throw new Error(assetRes.data.error || "Failed to publish asset");
      }
    }

    const response = await axios.post(`${API_BASE_URL}/offer/publish`, {
      custom_offer: selectedOffer.value.contentData,
    });

    if (response.data.success) {
      console.log("Posted");

      // storeSelectedPolicy.value.metadata.additionalProp1.inConnector = true;
      // membershipPolicy.value.metadata.additionalProp1.inConnector = true;
      // selectedAsset.value.metadata.additionalProp1.inConnector = true;
      // selectedOffer.value.metadata.additionalProp1.inConnector = true;

      // await axios.post(
      //   `${API_BASE_URL}/repository/update/full`,
      //   storeSelectedPolicy.value
      // );

      // await axios.post(
      //   `${API_BASE_URL}/repository/update/full`,
      //   selectedAsset.value
      // );

      // await axios.post(
      //   `${API_BASE_URL}/repository/update/full`,
      //   selectedOffer.value
      // );

      // await axios.post(
      //   `${API_BASE_URL}/repository/update/full`,
      //   membershipPolicy.value
      // );

      await loadProviderAssets();
      await loadContractDefinitions();
      closeCatalogsModal();

      // const message =
      //   selectedGlobalCatalogs.length > 0
      //     ? `Asset published to Local Catalog. Global catalog integration will be available soon.`
      //     : "Asset published successfully to Local Catalog!";

      // alert(message);
    } else {
      throw new Error(response.data.error || "Failed to publish asset");
    }

    if (selectedGlobalCatalogs.length > 0) {
      await new Promise((resolve) => setTimeout(resolve, 10000));
      console.log(
        `Global catalog publishing selected for: ${selectedGlobalCatalogs
          .map((c) => c.name)
          .join(", ")}`
      );

      console.log("List of catalogs: ", selectedGlobalCatalogs);

      const catalogRecord = buildSelectedGlobalMetadata();
      assetsToPublish.store.contentData.properties["ds2:CatalogMetadata"] =
        catalogRecord;

      console.log(
        "Edited asset to publish to gcat is: ",
        assetsToPublish.store.contentData
      );

      const response = await axios.post(`${API_BASE_URL}/assets/edit`, {
        asset: assetsToPublish.store.contentData,
      });

      if (response.data.success) {
        await axios.post(
          `${API_BASE_URL}/repository/update/full`,
          assetsToPublish.store
        );
      }

      await ensureCatalogSnapshot();
      const snapshot = catalogSnapshot.value;
      console.log(
        "Catalog snapshot for global publish: ",
        JSON.stringify(snapshot, null, 2)
      );

      if (!snapshot) {
        throw new Error("Unable to load global catalog snapshot.");
      }

      assetsToPublish.store.contentData.properties = {
        "ds2:CatalogMetadata": catalogRecord,
      };

      console.log(
        "Selected asset with catalog metadata: ",
        JSON.stringify(assetsToPublish.store, null, 2)
      );

      const globalPayload = buildGlobalCatalogPayload(snapshot);
      if (!globalPayload) {
        throw new Error("Failed to build global catalog payload.");
      }
      console.log(
        "Payload for global catalog request: ",
        JSON.stringify(globalPayload, null, 2)
      );

      selectedGlobalCatalogs.forEach(async (catalog) => {
        console.log(
          `Publishing to global catalog: ${catalog.name} at ${catalog.endpoint} with protocol ${catalog.protocol}`
        );
        const publishResponse = await axios.post(
          `${API_BASE_URL}/global-catalog/publish`,
          { globalPayload: globalPayload, endpoint: catalog.endpoint },
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );
        console.log("Global catalog publish response: ", publishResponse);
        if (publishResponse.data.success || publishResponse.status == 200) {
          toast.add({
            severity: "success",
            summary: "Info",
            detail: "Asset Published to Global Catalog successfully",
            life: 5000,
          });
        } else {
          toast.add({
            severity: "error",
            summary: "Error Message",
            detail: "Failed to publish asset to Global Catalog",
            life: 3000,
          });
        }
      });
    }
  } catch (error) {
    console.error("Error publishing asset:", error);
    alert(
      error.response?.data?.detail || error.message || "Failed to publish asset"
    );
  } finally {
    isPublishing.value = false;
  }
};

const handlePublishAction = async () => {
  console.log("Handling publish action, current step:", publishStep.value);

  if (publishStep.value === "select") {
    if (selectedCatalogsCount.value === 0) {
      alert("Please select at least one catalog to publish to.");
      return;
    }
    publishStep.value = "confirm";
    if (selectedGlobalCatalogs.value.length > 0) {
      //await ensureCatalogSnapshot();
    }
    return;
  }
  await publishToCatalogs();
};

const toggleShowPreview = () => {
  showPreview.value = !showPreview.value;
};

const loadContractDefinitions = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/contractdefinitions`);
    contractDefinitions.value = response.data;
    console.log("Contract definitions are: ", contractDefinitions.value);
  } catch (error) {
    console.error("Error fetching contract definitions:", error);
  }
};

const loadContractNegotiations = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/contractnegotiations`);
    contractNegotiations.value = response.data;
  } catch (error) {
    console.error("Error fetching contract negotiations:", error);
  }
};

const loadUser = async () => {
  console.log("Attempting to load user with ID:", user);
  console.log(userService.getCookieUser());

  try {
    const res = await axios.post(`${API_BASE_URL}/login`, {
      username: user,
    });
  } catch (err) {
    console.log("Login error:", err);
  }
};

const convertToEdcPolicy = (policy) => {
  return {
    "@context": {
      "@vocab": "https://w3id.org/edc/v0.0.1/ns/",
    },

    "@id": policy["@id"],

    "@type": "PolicyDefinition",

    policy: {
      ...policy,
    },
  };
};

function isEdcPolicy(policy) {
  console.log("Checking if policy is in EDC format:", policy);

  return (
    policy &&
    typeof policy === "object" &&
    policy.hasOwnProperty("policy") &&
    policy.policy &&
    typeof policy.policy === "object"
  );
}

// FIXED: Load participant data with better credential handling
const loadParticipantData = async () => {
  try {
    loading.value.credentials = true;
    loading.value.assets = true;

    const credsRes = await axios.get(
      `${API_BASE_URL}/credentials?participant_id=${encodeURIComponent(
        selectedParticipant.value
      )}`
    );

    console.log("Raw credentials response:", credsRes.data);

    // Handle different credential response structures
    if (Array.isArray(credsRes.data)) {
      credentials.value = credsRes.data;
    } else if (credsRes.data.credentials) {
      credentials.value = credsRes.data.credentials;
    } else if (credsRes.data.data) {
      credentials.value = credsRes.data.data;
    } else {
      credentials.value = [];
    }

    console.log("Processed credentials:", credentials.value);

    // Also load assets for this participant
    // const assetsRes = await axios.get(
    //   `${API_BASE_URL}/assets?participant_id=${encodeURIComponent(
    //     selectedParticipant.value
    //   )}`
    // );

    // if (Array.isArray(assetsRes.data)) {
    //   assets.value = assetsRes.data;
    // } else if (assetsRes.data.assets) {
    //   assets.value = assetsRes.data.assets;
    // } else if (assetsRes.data.data) {
    //   assets.value = assetsRes.data.data;
    // } else {
    //   assets.value = [];
    // }
  } catch (error) {
    console.error("Error fetching participant data:", error);
    alert(
      "Failed to load data. Make sure the backend server is running on " +
        API_BASE_URL
    );
  } finally {
    loading.value.credentials = false;
    loading.value.assets = false;
  }
};

const loadParticipants = async () => {
  try {
    loading.value.participants = true;
    const response = await axios.get(`${API_BASE_URL}/participants`);
    if (response.data && Array.isArray(response.data)) {
      participants.value = response.data.map((p) => ({
        id: p.participantContextId,
        name: p.did.split(":").pop() || p.participantContextId,
      }));
      if (!participants.value.length) {
        participants.value = [
          {
            id: "did:web:consumer-identityhub%3A7083:consumer",
            name: "Default Consumer",
          },
        ];
      }
      const exists = participants.value.some(
        (p) => p.id === selectedParticipant.value
      );
      if (!exists && participants.value.length)
        selectedParticipant.value = participants.value[0].id;
    }
  } catch (error) {
    console.error("Error fetching participants:", error);
    participants.value = [
      {
        id: "did:web:consumer-identityhub%3A7083:consumer",
        name: "Default Consumer",
      },
    ];
  } finally {
    loading.value.participants = false;
  }
};

// Watch for participant changes to determine default view
watch(selectedParticipant, async (newParticipantId) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/participants/roles/${encodeURIComponent(
        newParticipantId
      )}`
    );
    const roles = response.data.roles;

    if (roles.includes("provider")) {
      currentView.value = "provider";
      loadProviderAssets();
    } else {
      currentView.value = "subscriber";
      loadParticipantData();
    }
  } catch (error) {
    console.error("Error determining roles:", error);
    currentView.value = "subscriber";
    loadParticipantData();
  }
});

const loadAssets = async () => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/repository/find/prop/${user}`,
      {
        objectType: {
          name: "Asset",
        },
      },
      {
        params: {
          includeContent: "true",
          pageNumber: 1,
          pageSize: 100,
          sortBy: "_id",
        },
      }
    );
    console.log("Full response from API:", response.data);

    storeAssets.value = response.data;
    console.log("Loaded store assets:", storeAssets.value);
    dlmStoreStatus.value = {
      state: "online",
      message: "Online",
      lastChecked: new Date(),
    };
  } catch (error) {
    console.error("Failed to load store assets:", error?.response.data?.detail);
    toast.add({
      severity: "error",
      summary: "Error Message",
      detail: error?.response.data?.detail,
      life: 3000,
    });
    dlmStoreStatus.value = {
      state: "offline",
      message: "Offline",
      lastChecked: new Date(),
    };
  }
};

const loadOffers = async () => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/repository/find/prop/${user}`,
      {
        objectType: {
          name: "offer",
        },
      },
      {
        params: {
          includeContent: "true",
          pageNumber: 1,
          pageSize: 100,
          sortBy: "_id",
        },
      }
    );
    console.log("Full response from API:", response.data);

    storeOffers.value = response.data.filter(
      (item) =>
        item.objectType.name === "offer" || item.objectType.name === "Offer"
    );
    console.log("Loaded store offers:", storeOffers.value);
    dlmStoreStatus.value = {
      state: "online",
      message: "Online",
      lastChecked: new Date(),
    };
  } catch (error) {
    console.error("Failed to load store offers:", error);
    toast.add({
      severity: "error",
      summary: "Error Message",
      detail: error?.response.data?.detail,
      life: 3000,
    });
    dlmStoreStatus.value = {
      state: "offline",
      message: "Offline",
      lastChecked: new Date(),
    };
  }
};

const loadAllOffers = async () => {
  await loadOffers();
  await loadContractDefinitions();
};

const loadAllPolicies = async () => {
  await loadStorePolicies();
  await loadPolicies();
};

// Mount
onMounted(async () => {
  loadStorePolicies();
  loadParticipants();
  loadGlobalCatalogs();

  loadUser();
  loadProviderAssets();
  loadPolicies();
  loadAssets();
  loadOffers();
  loadContractDefinitions();
  loadContractNegotiations();
  loadConnectorInfo();
  loadParticipantData();
});
</script>

<style>
:root {
  --bg: #0b1020;
  --bg2: #0e1428;
  --card: rgba(255, 255, 255, 0.06);
  --card-soft: rgba(255, 255, 255, 0.04);
  --stroke: rgba(255, 255, 255, 0.12);
  --txt: #e8eefc;
  --muted: #666;
  --brand: #009688;
  --brand-2: #009688;
  --ok: #009688;
  --warn: #ff9800;
  --err: #f44336;
  --shadow: 0 10px 30px rgba(0, 0, 0, 0.25);
  --rad: 16px;
}
</style>

<style scoped>
/* ===== Base / Theme ===== */

* {
  box-sizing: border-box;
}
html,
body {
  margin: 0;
  background: #f5f7fa;
  color: #333;
  font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto,
    Helvetica, Arial;
}
.provider-dashboard-container {
  font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
  color: #333;
  background-color: #f5f7fa;
  min-height: 100vh;
  margin-left: 250px; /* Default sidebar width */
  width: calc(100% - 250px);
  transition: all 0.3s ease;
}

.provider-dashboard-container.sidebar-collapsed {
  margin-left: 70px;
  width: calc(100% - 70px);
}

.provider-dashboard-container.displayNone {
  margin-left: 0;
  width: 100%;
}

.gradient-bg {
  background: radial-gradient(
      1200px 600px at 20% -10%,
      #1b2a4a 0%,
      transparent 60%
    ),
    radial-gradient(1000px 600px at 80% 10%, #0b2e2b 0%, transparent 60%),
    linear-gradient(180deg, var(--bg) 0%, var(--bg2) 100%);
  min-height: 100vh;
}

/* ===== Glass Surfaces ===== */
.glass {
  background: white;
  border: 1px solid #e0e0e0;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-radius: var(--rad);
  box-shadow: var(--shadow);
}

.modal-glass {
  background: white;
  border: 1px solid #e0e0e0;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-radius: var(--rad);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
}

/* .glass-header::before {
  background: #009688;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-radius: 0;
  box-shadow: var(--shadow);
} */

.glass-header {
  background: #009688;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-radius: 0;
  box-shadow: var(--shadow);
}

.glass-soft {
  background: #f9f9f9;
  border: 1px dashed #e0e0e0;
  border-radius: calc(var(--rad) - 4px);
}

/* ===== Topbar ===== */
.topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  gap: 16px;
  position: fixed;
  width: 100%; /* Full width */
  margin: 0; /* Remove margins */
  box-sizing: border-box; /* Include padding in width */
  padding: 30px;
  z-index: 5;
  top: 0;
  left: 0;
  width: 100%;
  background: #009688;
}

.topbar__left {
  display: flex;
  gap: 12px;
  align-items: center;
}
.logo-badge {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: grid;
  place-items: center;
  background: linear-gradient(135deg, #4caf50, #45a049);
  color: white;
  font-weight: 800;
  letter-spacing: 0.5px;
}
.brand h1 {
  font-size: 25px;
  margin: 0;
  color: white;
}
.brand p {
  margin: 2px 0 0;
  font-size: 12px;
  color: white;
}

.topbar__right {
  display: flex;
  gap: 12px;
  align-items: center;
}
.participant-select {
  display: grid;
  gap: 6px;
}
.participant-select label {
  font-size: 12px;
  color: white;
}
.select-wrap {
  position: relative;
}
.select-wrap select {
  appearance: none;
  background: white;
  border: 1px solid #ddd;
  color: #333;
  padding: 10px 36px 10px 12px;
  min-width: 260px;
  border-radius: 10px;
}
.tiny-loading {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  gap: 4px;
}
.tiny-loading .dot {
  width: 6px;
  height: 6px;
  background: #4caf50;
  border-radius: 50%;
  animation: blink 1.2s infinite;
}
.tiny-loading .dot:nth-child(2) {
  animation-delay: 0.2s;
}
.tiny-loading .dot:nth-child(3) {
  animation-delay: 0.4s;
}
@keyframes blink {
  0%,
  80%,
  100% {
    opacity: 0.2;
  }
  40% {
    opacity: 1;
  }
}

/* ===== Buttons ===== */
.btn {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  border-radius: 12px;
  padding: 10px 14px;
  font-weight: 600;
  border: 1px solid transparent;
  transition: transform 0.06s ease, background 0.2s ease, border-color 0.2s ease;
}

.btn-header {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  border-radius: 12px;
  padding: 10px 14px;
  font-weight: 600;
  border: 1px solid transparent;
  transition: transform 0.06s ease, background 0.2s ease, border-color 0.2s ease;
}

.btn-primary {
  background: #4caf50;
  color: white;
}
.btn-ghost {
  background: transparent;
  color: #4caf50;
  border-color: #4caf50;
}
.btn-ghost-preview {
  background: transparent;
  color: #4caf50;
  border-color: #4caf50;
  margin-left: auto; /* pushes it to the far right */
}

.btn:hover {
  transform: translateY(-1px);
}
.btn-disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.inline-loading {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

/* icon */
.i {
  width: 18px;
  height: 18px;
  fill: none;
  stroke: currentColor;
  stroke-width: 2;
}
.icon-btn {
  width: 38px;
  height: 38px;
  display: grid;
  place-items: center;
  border-radius: 10px;
  background: #f5f5f5;
  border: 1px solid #e0e0e0;
  color: #333;
}
.icon-btn--ghost {
  background: transparent;
  border-color: transparent;
}

/* ===== Layout ===== */
.container {
  width: 100%;
  margin-left: 0px;
  padding: 2rem;
  min-height: calc(100vh - 80px);
  margin-top: 80px; /* Account for navbar height */
}
.container.displayNone {
  width: 100%;
  height: 100%;
  margin-top: 100px; /* Account for navbar height */
}
.panel {
  padding: 16px;
  margin: 10px 0 14px;
}
.panel--header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}
.panel__title {
  display: flex;
  align-items: center;
  gap: 10px;
}
.panel__title h2 {
  margin: 0;
  font-size: 18px;
  color: #333;
}
.catalog-url {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.code-pill {
  background: #f5f5f5;
  padding: 6px 10px;
  border-radius: 10px;
  border: 1px solid #e0e0e0;
  color: #333;
}
.muted {
  color: #666;
}

/* Chips */
.chip {
  background: #f5f5f5;
  border: 1px solid #e0e0e0;
  padding: 4px 8px;
  border-radius: 999px;
  font-size: 12px;
  color: #333;
}
.chip--soft {
  background: #f9f9f9;
}
.chip--ok {
  background-color: #e8f5e8;
  color: #4caf50;
}
.chip--warn {
  background: #fff3e0;
  border-color: #ffb74d;
  color: #ff9800;
}

/* ===== Grid & Cards ===== */
.grid {
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
}
.card {
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: white;
  border-radius: 12px;
  border: 1px solid #e0e0e0;
}
.card--asset {
  min-height: 220px;
}
.card__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
}
.card__header .title h3 {
  margin: 0 0 4px;
  font-size: 16px;
  color: #333;
}
.card__header .meta {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.desc {
  margin: 2px 0 8px;
  color: #666;
}

.req {
  padding: 12px;
  margin-top: 4px;
}
.req h4 {
  margin: 0 0 10px;
  font-size: 13px;
  color: #333;
}
.req__list {
  display: grid;
  gap: 8px;
}
.req__item {
  display: grid;
  grid-template-columns: 28px 1fr;
  gap: 8px;
  padding: 10px;
  border-radius: 12px;
  background: #f9f9f9;
  border: 1px solid #e0e0e0;
}
.req__item.missing {
  background: #ffebee;
  border-color: #ffcdd2;
}
.req__icon {
  width: 28px;
  height: 28px;
  display: grid;
  place-items: center;
  border-radius: 8px;
  background: #f5f5f5;
  color: #4caf50;
}
.req__name {
  font-weight: 700;
  color: #333;
}
.req__constraint {
  font-size: 12px;
  color: #666;
  margin-top: 4px;
}

.card__footer {
  margin-top: auto;
  display: flex;
  justify-content: flex-end;
}

/* Empty */
.empty {
  padding: 40px;
  text-align: center;
}
.empty .emoji {
  font-size: 40px;
  margin-bottom: 8px;
}

/* ===== Modal ===== */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: grid;
  place-items: center;
  z-index: 50;
  padding: 16px;
}
.modal__footer {
  padding-top: 16px;
}
.modal {
  width: 100%;
  max-width: 980px;
  background: white;
}
.modal--xl {
  max-width: 1040px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
}
.modal--lg {
  max-width: 860px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
}
.modal__header {
  padding: 16px;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.modal__title {
  display: flex;
  gap: 12px;
  align-items: center;
  color: #333;
}
.policy-preview {
  padding-top: 16px;
}
.policy-btn {
  display: flex;
}
.badge {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: grid;
  place-items: center;
  background-color: #e8f5e8;
  color: #4caf50;
}
.badge.pulse {
  box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.6);
  animation: pulse 2s infinite;
}
@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.6);
  }
  70% {
    box-shadow: 0 0 0 16px rgba(76, 175, 80, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0);
  }
}

.modal__content {
  padding: 16px;
  overflow-y: auto;
  padding-right: 0.5rem;
  overflow-x: auto;
  color: #333;
}

/* Contract progress */
.progress {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  margin-bottom: 16px;
}
.progress__step {
  text-align: center;
  display: grid;
  gap: 6px;
  justify-items: center;
}
.progress__step .dot {
  width: 28px;
  height: 28px;
  border-radius: 999px;
  display: grid;
  place-items: center;
  background: #f5f5f5;
  border: 1px solid #e0e0e0;
  color: #333;
}
.progress__step.active .dot {
  background: linear-gradient(135deg, #4caf50, #45a049);
  color: white;
  font-weight: 800;
}
.progress__step.done .dot {
  background: rgba(76, 175, 80, 0.22);
  border-color: rgba(76, 175, 80, 0.35);
}
.status {
  display: grid;
  place-items: center;
  gap: 8px;
  padding: 20px;
}
.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(0, 0, 0, 0.15);
  border-top-color: #4caf50;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}
.spinner--sm {
  width: 18px;
  height: 18px;
  border-width: 2px;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.result {
  padding: 16px;
  border-radius: var(--rad);
}
.result--error {
  border: 1px solid rgba(244, 67, 54, 0.35);
  background: rgba(244, 67, 54, 0.08);
}
.result--ok {
  border: 1px solid rgba(76, 175, 80, 0.35);
  background: rgba(76, 175, 80, 0.08);
}
.big-icon {
  font-size: 42px;
  margin-bottom: 6px;
}
.big-icon.ok {
  color: #4caf50;
}

.data-preview {
  margin-top: 16px;
}
.data-preview h4 {
  margin: 0 0 8px;
  color: #333;
}

/* Wizard */
.wizard {
  padding: 8px 16px 16px;
}
.wizard__steps {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 10px;
  margin: 12px 0 10px;
}
.wizard__divider {
  height: 2px;
  background: #e0e0e0;
  border-radius: 2px;
}
.wizard__step {
  display: flex;
  gap: 8px;
  align-items: center;
  color: #666;
}
.wizard__step .dot {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  border: 1px solid #e0e0e0;
  background: #f5f5f5;
  color: #333;
}
.wizard__step.active {
  color: #333;
}
.wizard__step.active .dot {
  background: linear-gradient(135deg, #4caf50, #45a049);
  color: white;
  font-weight: 800;
}
.wizard__step.done .dot {
  background: rgba(76, 175, 80, 0.22);
  border-color: rgba(76, 175, 80, 0.35);
}
.wizard__content {
  margin-top: 20px;
}

.form-grid {
  display: grid;
  gap: 12px;
  grid-template-columns: 1fr 1fr;
}
.form-item {
  display: grid;
  gap: 6px;
}
.custom-input {
  border: 1px solid #ddd;
  border-radius: 10px;
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  color: #333;
  background-color: #fff;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.custom-input:focus {
  border-color: #4caf50;
  box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.2);
}
.form-item--full {
  grid-column: 1 / -1;
}
label {
  font-size: 12px;
  color: #666;
}
.req {
  color: #f44336;
}
input,
textarea {
  width: 100%;
  background: white;
  border: 1px solid #ddd;
  color: #333;
  padding: 10px 12px;
  border-radius: 10px;
}
textarea {
  resize: vertical;
}

.policy-editor {
  display: grid;
  gap: 8px;
}
.policy-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.codearea {
  width: 100%;
  background: #f5f5f5;
  color: #333;
  border: 1px solid #ddd;
  border-radius: 12px;
  padding: 12px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
    "Liberation Mono", "Courier New", monospace;
  line-height: 1.4;
  outline: none;
}
.error-text {
  color: #f44336;
  font-size: 13px;
}

.wizard__footer {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 12px;
}
.wizard__footer .spacer {
  flex: 1;
}

.alert {
  margin-top: 12px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid #e0e0e0;
}
.alert--ok {
  background: #e8f5e8;
  border-color: #c8e6c9;
}
.alert--err {
  background: #ffebee;
  border-color: #ffcdd2;
}

/* ===== Transitions ===== */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.18s ease;
}
.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.18s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
.slide-enter-active,
.slide-leave-active {
  transition: all 0.22s ease;
}
.slide-enter-from {
  transform: translateY(6px);
  opacity: 0;
}
.slide-leave-to {
  transform: translateY(6px);
  opacity: 0;
}

/* Tabs styling */
.tab-navigation {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 0.5rem;
  margin: 20px;
}

.tab-btn {
  padding: 0.8rem 1.5rem;
  border-radius: 8px 8px 0 0;
  border: none;
  background: transparent;
  color: #666;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.tab-btn:hover {
  color: #4caf50;
  background: rgba(76, 175, 80, 0.05);
}

.tab-btn.active {
  color: #4caf50;
  background: white;
  box-shadow: 0 -2px 0 #4caf50 inset;
}

/* Small screens */
@media (max-width: 820px) {
  .form-grid {
    grid-template-columns: 1fr;
  }
  .topbar {
    flex-direction: column;
    align-items: stretch;
  }
  .topbar__right {
    justify-content: space-between;
  }
}

/* Enhanced Contract Modal Styles */
.contract-modal-header {
  background: linear-gradient(135deg, #009688 0%, #00695c 100%);
  color: white;
}

.contract-modal-header .badge {
  background: rgba(255, 255, 255, 0.2);
  color: white;
}

.contract-modal-header h2 {
  color: white;
}

.contract-modal-header .muted {
  color: rgba(255, 255, 255, 0.8);
}

/* Configuration Section */
.configuration-section {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 0;
  margin-bottom: 20px;
  color: #333;
  font-size: 18px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.static-field {
  padding: 10px 12px;
  background: #f9f9f9;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  color: #333;
  font-size: 14px;
}

.static-field.code {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 13px;
}

.endpoint-url {
  word-break: break-all;
}

/* Policy Selection */
.policy-selection {
  margin-top: 15px;
}

.policy-options {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.policy-option {
  padding: 16px;
  border: 2px solid #e0e0e0;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  background: white;
}

.policy-option:hover {
  border-color: #4caf50;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.1);
}

.policy-option.selected {
  border-color: #4caf50;
  background: linear-gradient(135deg, #f1f8e9 0%, #e8f5e9 100%);
}

.policy-option__header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.policy-option__radio {
  width: 20px;
  height: 20px;
  border: 2px solid #ddd;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.radio-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: transparent;
  transition: background 0.2s ease;
}

.radio-dot.selected {
  background: #4caf50;
}

.policy-option__title {
  flex: 1;
}

.policy-option__title h4 {
  margin: 0;
  color: #333;
}

.policy-type {
  font-size: 12px;
  color: #666;
}

.policy-option__preview {
  padding: 12px;
  background: rgba(0, 0, 0, 0.02);
  border-radius: 8px;
  font-size: 13px;
}

.policy-details {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

/* Credentials Check */
.credentials-check {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.credential-item {
  display: flex;
  align-items: center;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  background: #f9f9f9;
  transition: all 0.2s ease;
}

.credential-item.valid {
  border-left: 4px solid #4caf50;
  background: #e8f5e8;
}

.credential-item.missing {
  border-left: 4px solid #f44336;
  background: #ffebee;
}

.credential-icon {
  margin-right: 12px;
  font-size: 20px;
}

.credential-item.valid .credential-icon {
  color: #4caf50;
}

.credential-item.missing .credential-icon {
  color: #f44336;
}

.credential-details {
  flex: 1;
}

.credential-name {
  display: block;
  font-weight: 600;
  color: #333;
}

.credential-status {
  display: block;
  font-size: 12px;
  color: #666;
}

.credential-constraint {
  font-size: 11px;
  padding: 4px 8px;
  background: rgba(0, 0, 0, 0.05);
  border-radius: 4px;
  color: #666;
}

.warning-message {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
  margin-top: 15px;
  background: #fff3e0;
  border: 1px solid #ffb74d;
  border-radius: 8px;
  color: #ff9800;
}

.warning-message i {
  font-size: 18px;
}

.success-message {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
  margin-top: 15px;
  background: #e8f5e8;
  border: 1px solid #c8e6c9;
  border-radius: 8px;
  color: #4caf50;
}

.success-message i {
  font-size: 18px;
  color: #4caf50;
}

/* Progress Stages */
.contract-progress,
.transfer-progress {
  padding: 10px 0;
}

.progress-header {
  text-align: center;
  margin-bottom: 30px;
}

.progress-header h3 {
  margin: 0 0 8px;
  color: #333;
}

.progress-stages,
.transfer-stages {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.stage {
  display: flex;
  align-items: flex-start;
  padding: 20px;
  border-radius: 12px;
  border: 1px solid #e0e0e0;
  background: white;
  transition: all 0.3s ease;
}

.stage.active {
  border-color: #4caf50;
  background: linear-gradient(
    135deg,
    rgba(76, 175, 80, 0.1),
    rgba(76, 175, 80, 0.05)
  );
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.1);
}

.stage.completed {
  border-color: rgba(76, 175, 80, 0.3);
  background: rgba(76, 175, 80, 0.05);
}

.stage-icon {
  position: relative;
  margin-right: 20px;
}

.stage-number {
  position: absolute;
  top: -8px;
  right: -8px;
  width: 24px;
  height: 24px;
  background: #4caf50;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 800;
}

.stage.completed .stage-number {
  background: #4caf50;
  color: white;
}

.stage-icon i {
  font-size: 28px;
  color: #666;
}

.stage.active .stage-icon i {
  color: #4caf50;
}

.stage.completed .stage-icon i {
  color: #4caf50;
}

.stage-content {
  flex: 1;
}

.stage-content h4 {
  margin: 0 0 8px;
  color: #333;
  font-size: 16px;
}

.stage-content p {
  margin: 0 0 12px;
  color: #666;
  font-size: 13px;
}

.stage-details {
  background: #f9f9f9;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 12px;
  margin-top: 10px;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  margin-bottom: 6px;
  font-size: 13px;
}

.detail-item:last-child {
  margin-bottom: 0;
}

.detail-item span:first-child {
  color: #666;
  font-weight: 500;
}

.detail-item code {
  background: rgba(0, 0, 0, 0.05);
  padding: 2px 6px;
  border-radius: 4px;
  font-family: ui-monospace, monospace;
  color: #4caf50;
}

.status-text {
  color: #ff9800;
  font-weight: 500;
}

.stage-status {
  margin-left: 20px;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.status-badge.processing {
  background: #e3f2fd;
  color: #1976d2;
  border: 1px solid #1976d2;
}

.status-badge.completed {
  background: #e8f5e8;
  color: #4caf50;
  border: 1px solid #4caf50;
}

.status-badge.pending {
  background: #fff3e0;
  color: #ff9800;
  border: 1px solid #ff9800;
}

/* Progress Bar */
.progress-bar-container {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 10px;
}

.progress-bar {
  flex: 1;
  height: 8px;
  background: #f5f5f5;
  border-radius: 4px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #4caf50, #45a049);
  border-radius: 4px;
  transition: width 0.3s ease;
}

.progress-text {
  font-size: 12px;
  font-weight: 600;
  color: #4caf50;
  min-width: 40px;
}

/* Controls */
.progress-controls,
.transfer-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid #e0e0e0;
}

.progress-info,
.transfer-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #666;
}

.info-item i {
  font-size: 14px;
}

/* Completion View */
.completion-view {
  padding: 10px 0;
}

.completion-header {
  text-align: center;
  margin-bottom: 30px;
}

.success-icon {
  font-size: 60px;
  color: #4caf50;
  margin-bottom: 15px;
}

.completion-details {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.detail-card {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  padding: 20px;
}

.detail-card h4 {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 0;
  margin-bottom: 20px;
  color: #333;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 15px;
}

.detail-item {
  display: flex;
  flex-direction: column;
}

.detail-item .label {
  font-size: 12px;
  color: #666;
  margin-bottom: 4px;
}

.detail-item .value {
  font-size: 14px;
  color: #333;
  word-break: break-all;
}

.detail-item .value.code {
  font-family: ui-monospace, monospace;
  background: #f9f9f9;
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 12px;
}

.status-completed {
  color: #4caf50;
  font-weight: 600;
}

.data-preview-section {
  margin-top: 20px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.data-stats {
  display: flex;
  gap: 15px;
}

.stat {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #666;
}

.data-actions {
  display: flex;
  gap: 10px;
  margin-top: 15px;
  justify-content: flex-end;
}

.completion-actions {
  display: flex;
  justify-content: space-between;
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid #e0e0e0;
}

/* Error View */
.error-view {
  padding: 10px 0;
}

.error-header {
  text-align: center;
  margin-bottom: 30px;
}

.error-icon {
  font-size: 60px;
  color: #f44336;
  margin-bottom: 15px;
}

.error-details {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.error-card {
  background: #ffebee;
  border: 1px solid #ffcdd2;
  border-radius: 12px;
  padding: 20px;
}

.error-message {
  background: rgba(0, 0, 0, 0.05);
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 15px;
  color: #f44336;
  font-family: ui-monospace, monospace;
  font-size: 12px;
  white-space: pre-wrap;
  max-height: 200px;
  overflow-y: auto;
}

.troubleshooting {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  padding: 20px;
}

.troubleshooting h4 {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 0;
  margin-bottom: 15px;
  color: #333;
}

.troubleshooting ul {
  margin: 0;
  padding-left: 20px;
  color: #666;
}

.troubleshooting li {
  margin-bottom: 8px;
  font-size: 13px;
}

.troubleshooting li:last-child {
  margin-bottom: 0;
}

.error-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid #e0e0e0;
}

/* ===== Modal ===== */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(2, 6, 23, 0.6);
  display: grid;
  place-items: center;
  z-index: 50;
  padding: 16px;
}
.modal__footer {
  padding-top: 16px;
}
.modal {
  width: 100%;
  max-width: 980px;
}
.modal--xl {
  max-width: 1040px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
}
.modal--lg {
  max-width: 860px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
}
.modal__header {
  padding: 16px;
  border-bottom: 1px solid var(--stroke);
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.modal__title {
  display: flex;
  gap: 12px;
  align-items: center;
  color: black;
}
.policy-preview {
  padding-top: 16px;
}
.policy-btn {
  display: flex;
}
.badge {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: grid;
  place-items: center;
  background-color: #ecfdf5;
  color: #047857;
}
.badge.pulse {
  box-shadow: 0 0 0 0 rgba(108, 243, 214, 0.6);
  animation: pulse 2s infinite;
}
@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(108, 243, 214, 0.6);
  }
  70% {
    box-shadow: 0 0 0 16px rgba(108, 243, 214, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(108, 243, 214, 0);
  }
}

.modal__content {
  padding: 16px;
  overflow-y: auto;
  padding-right: 0.5rem;
  overflow-x: auto;
}

/* Contract progress */
.progress {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  margin-bottom: 16px;
}
.progress__step {
  text-align: center;
  display: grid;
  gap: 6px;
  justify-items: center;
}
.progress__step .dot {
  width: 28px;
  height: 28px;
  border-radius: 999px;
  display: grid;
  place-items: center;
  background: var(--card);
  border: 1px solid var(--stroke);
}
.progress__step.active .dot {
  background: linear-gradient(135deg, var(--brand), var(--brand-2));
  color: #06111d;
  font-weight: 800;
}
.progress__step.done .dot {
  background: rgba(24, 201, 100, 0.22);
  border-color: rgba(24, 201, 100, 0.35);
}
.status {
  display: grid;
  place-items: center;
  gap: 8px;
  padding: 20px;
}
.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(255, 255, 255, 0.15);
  border-top-color: var(--brand);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}
.spinner--sm {
  width: 18px;
  height: 18px;
  border-width: 2px;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.result {
  padding: 16px;
  border-radius: var(--rad);
}
.result--error {
  border: 1px solid rgba(255, 93, 117, 0.35);
  background: rgba(255, 93, 117, 0.08);
}
.result--ok {
  border: 1px solid rgba(24, 201, 100, 0.35);
  background: rgba(24, 201, 100, 0.08);
}
.big-icon {
  font-size: 42px;
  margin-bottom: 6px;
}
.big-icon.ok {
  color: var(--ok);
}

.data-preview {
  margin-top: 16px;
}
.data-preview h4 {
  margin: 0 0 8px;
}

/* Wizard */
.wizard {
  padding: 8px 16px 16px;
}
.wizard__steps {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 10px;
  margin: 12px 0 10px;
}
.wizard__divider {
  height: 2px;
  background: var(--stroke);
  border-radius: 2px;
}
.wizard__step {
  display: flex;
  gap: 8px;
  align-items: center;
  color: var(--muted);
}
.wizard__step .dot {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  border: 1px solid var(--stroke);
  background: var(--card);
}
.wizard__step.active {
  color: black;
}
.wizard__step.active .dot {
  background: linear-gradient(135deg, var(--brand), var(--brand-2));
  color: #06111d;
  font-weight: 800;
}
.wizard__step.done .dot {
  background: rgba(24, 201, 100, 0.22);
  border-color: rgba(24, 201, 100, 0.35);
}
.wizard__content {
  margin-top: 20px;
}

.form-grid {
  display: grid;
  gap: 12px;
  grid-template-columns: 1fr 1fr;
}
.form-item {
  display: grid;
  gap: 6px;
}
.custom-input {
  border: 1px solid black; /* Border color */
  border-radius: 10px; /* Rounded corners */
  padding: 0.5rem 0.75rem; /* Spacing inside the input */
  font-size: 0.875rem;
  color: #333; /* Text color */
  background-color: #fff; /* Background color */
  outline: none; /* Remove default outline */
  transition: border-color 0.2s, box-shadow 0.2s;
}

.custom-input:focus {
  border-color: #00796b; /* Darker border on focus */
  box-shadow: 0 0 0 2px rgba(0, 150, 136, 0.2); /* Optional focus glow */
}
.form-item--full {
  grid-column: 1 / -1;
}
label {
  font-size: 12px;
  color: var(--muted);
}
.req {
  color: var(--err);
}
input,
textarea {
  width: 100%;
  background: var(--card);
  border: 1px solid var(--stroke);
  color: var(--txt);
  padding: 10px 12px;
  border-radius: 10px;
}
textarea {
  resize: vertical;
}

.policy-editor {
  display: grid;
  gap: 8px;
}
.policy-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.codearea {
  width: 100%;
  background: #0b0f1e;
  color: #c8e6ff;
  border: 1px solid #1c2a4c;
  border-radius: 12px;
  padding: 12px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
    "Liberation Mono", "Courier New", monospace;
  line-height: 1.4;
  outline: none;
}
.error-text {
  color: var(--err);
  font-size: 13px;
}

.wizard__footer {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 12px;
}
.wizard__footer .spacer {
  flex: 1;
}

.alert {
  margin-top: 12px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid var(--stroke);
}
.alert--ok {
  background: rgba(24, 201, 100, 0.12);
  border-color: rgba(24, 201, 100, 0.35);
}
.alert--err {
  background: rgba(255, 93, 117, 0.12);
  border-color: rgba(255, 93, 117, 0.35);
}

/* ===== Transitions ===== */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.18s ease;
}
.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.18s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
.slide-enter-active,
.slide-leave-active {
  transition: all 0.22s ease;
}
.slide-enter-from {
  transform: translateY(6px);
  opacity: 0;
}
.slide-leave-to {
  transform: translateY(6px);
  opacity: 0;
}

/* Tabs styling */
.tab-navigation {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  border-bottom: 1px solid var(--light-gray);
  padding-bottom: 0.5rem;
  margin: 20px;
}

.tab-btn {
  padding: 0.8rem 1.5rem;
  border-radius: 8px 8px 0 0;
  border: none;
  background: transparent;
  color: var(--gray);
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.tab-btn:hover {
  color: #108e8a;
  background: rgba(67, 97, 238, 0.05);
}

.tab-btn.active {
  color: #108e8a;
  background: white;
  box-shadow: 0 -2px 0 #108e8a inset;
}

/* Small screens */
@media (max-width: 820px) {
  .form-grid {
    grid-template-columns: 1fr;
  }
  .topbar {
    flex-direction: column;
    align-items: stretch;
  }
  .topbar__right {
    justify-content: space-between;
  }
}
/* Catalogs Modal Styles */
.catalogs-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 20px;
}

.catalog-item {
  padding: 16px;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid rgba(255, 255, 255, 0.12);
}

.catalog-item:hover {
  border-color: rgba(108, 243, 214, 0.4);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.catalog-item--selected {
  border-color: #009688;
  background: linear-gradient(
    180deg,
    rgba(108, 243, 214, 0.08),
    rgba(108, 243, 214, 0.04)
  );
}

.catalog-item__header {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.catalog-item__checkbox {
  margin-top: 4px;
}

.checkbox-wrapper {
  position: relative;
  display: inline-block;
}

.checkbox-wrapper input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
}

.checkmark {
  position: relative;
  display: inline-block;
  width: 20px;
  height: 20px;
  background: rgba(255, 255, 255, 0.08);
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  transition: all 0.2s ease;
}

.checkbox-wrapper input:checked ~ .checkmark {
  background: #009688;
  border-color: #009688;
}

.checkbox-wrapper input:checked ~ .checkmark::after {
  content: "";
  position: absolute;
  left: 6px;
  top: 2px;
  width: 5px;
  height: 10px;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.catalog-item__info {
  flex: 1;
}

.catalog-item__title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.catalog-item__title h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: black;
}

.catalog-badge {
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.catalog-badge.local {
  background: rgba(108, 243, 214, 0.2);
  color: #6cf3d6;
  border: 1px solid rgba(108, 243, 214, 0.4);
}

.catalog-badge.global {
  background: rgba(67, 97, 238, 0.2);
  color: #4361ee;
  border: 1px solid rgba(67, 97, 238, 0.4);
}

.catalog-item__description {
  margin: 0;
  font-size: 13px;
  line-height: 1.4;
}

.catalog-item__status {
  margin-top: 4px;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

.status-connected {
  background: rgba(24, 201, 100, 0.15);
  color: #18c964;
  border: 1px solid rgba(24, 201, 100, 0.3);
}

.status-disconnected {
  background: rgba(255, 93, 117, 0.15);
  color: #ff5d75;
  border: 1px solid rgba(255, 93, 117, 0.3);
}

.status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
}

.catalog-item__details {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.catalog-meta {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.meta-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.meta-label {
  font-size: 11px;
  color: black;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.meta-value {
  font-size: 13px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  color: black;
  word-break: break-all;
}

/* Publish Summary */
.publish-summary {
  padding: 16px;
  margin-bottom: 20px;
  border-radius: 12px;
}

.publish-summary h4 {
  margin: 0 0 12px 0;
  font-size: 15px;
  color: #e8eefc;
}

.summary-content p {
  margin: 0 0 8px 0;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.8);
}

.selected-catalogs-list {
  margin: 0;
  padding-left: 20px;
}

.selected-catalogs-list li {
  margin: 4px 0;
  font-size: 13px;
  color: #e8eefc;
}

.catalog-type {
  font-size: 11px;
  color: black;
}

/* Publish Button Icon */
.publish-icon {
  width: 16px;
  height: 16px;
  fill: currentColor;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .catalog-meta {
    grid-template-columns: 1fr;
  }

  .catalog-item__header {
    flex-direction: column;
    gap: 8px;
  }

  .catalog-item__title {
    flex-wrap: wrap;
  }
}
/* Modal Container */
.modal--publish {
  max-width: 900px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  background: white;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  border: 1px solid #e0e0e0;
}

/* Modal Header - Green Theme */
.green-header {
  background: #009688;
  color: white;
}

.modal__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 24px 32px;
}

.modal__header-content {
  display: flex;
  align-items: center;
  gap: 16px;
}

.modal__icon {
  width: 48px;
  height: 48px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal__icon svg {
  width: 24px;
  height: 24px;
  stroke-width: 2;
  stroke: white;
}

.modal__title-section {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.modal__title {
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  color: white;
}

.modal__subtitle {
  margin: 0;
  font-size: 14px;
  opacity: 0.9;
  color: rgba(255, 255, 255, 0.9);
}

.asset-name {
  font-weight: 600;
  color: #e0f2f1;
}

.modal__close-btn {
  background: rgba(255, 255, 255, 0.15);
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  color: white;
}

.modal__close-btn:hover {
  background: rgba(255, 255, 255, 0.25);
  transform: rotate(90deg);
}

.modal__close-btn svg {
  width: 20px;
  height: 20px;
  stroke-width: 2;
  stroke: white;
}

/* Dialog Styles */
.publish-dialog {
  border-radius: 16px;
  overflow: hidden;
}

:deep(.p-dialog-content) {
  padding: 0;
}

:deep(.p-dialog-header) {
  padding: 0;
  border-bottom: none;
}

:deep(.p-dialog-footer) {
  padding: 24px 32px;
  border-top: 1px solid #f0f0f0;
  background: white;
  display: flex;
  justify-content: flex-end;
  gap: 16px;
}

.dialog-header {
  padding: 24px 32px;
  width: 100%;
}

.dialog-header-content {
  display: flex;
  align-items: center;
  gap: 16px;
}

.dialog-header__icon {
  width: 48px;
  height: 48px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.dialog-header__icon svg {
  width: 24px;
  height: 24px;
  stroke-width: 2;
  stroke: white;
}

.dialog-header__title-section {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.dialog-header__title {
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  color: white;
}

.dialog-header__subtitle {
  margin: 0;
  font-size: 14px;
  opacity: 0.9;
  color: rgba(255, 255, 255, 0.9);
}

.asset-name {
  font-weight: 600;
  color: #e0f2f1;
}

/* Dialog Content */
.dialog-content {
  padding: 32px;
  background: #fafafa;
  max-height: 60vh;
  overflow-y: auto;
}

/* Catalog Grid */
.catalog-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
  margin-bottom: 32px;
}

/* Catalog Card */
.catalog-card {
  position: relative;
  background: white;
  border-radius: 12px;
  padding: 20px;
  border: 1px solid #e0e0e0;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
  min-height: 240px;
  overflow: hidden;
}

.catalog-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 24px rgba(0, 150, 136, 0.1);
  border-color: #009688;
}

.catalog-card--selected {
  border-color: #009688;
  border-width: 2px;
  background: linear-gradient(135deg, #f1f8e9 0%, #e8f5e9 100%);
}

.catalog-card--local.catalog-card--selected {
  border-color: #00796b;
  background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%);
}

.catalog-card--global.catalog-card--selected {
  border-color: #004d40;
  background: linear-gradient(135deg, #e0f2f1 0%, #b2dfdb 100%);
}

.catalog-card--disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.catalog-card--disabled:hover {
  transform: none;
  box-shadow: none;
  border-color: #e0e0e0;
}

/* Card Header */
.catalog-card__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
}

.catalog-card__selection {
  z-index: 2;
}

.selection-indicator {
  width: 24px;
  height: 24px;
  border: 2px solid #bdbdbd;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.selection-indicator.selected {
  border-color: #009688;
  background: #009688;
}

.catalog-card--local .selection-indicator.selected {
  border-color: #00796b;
  background: #00796b;
}

.catalog-card--global .selection-indicator.selected {
  border-color: #004d40;
  background: #004d40;
}

.selection-check svg {
  width: 14px;
  height: 14px;
  stroke: white;
}

.catalog-card__badge {
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.catalog-card__badge.local {
  background: #00796b;
  color: white;
}

.catalog-card__badge.global {
  background: #004d40;
  color: white;
}

/* Card Body */
.catalog-card__body {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.catalog-card__icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
}

.catalog-card--local .catalog-card__icon {
  background: #e8f5e9;
  color: #00796b;
}

.catalog-card--global .catalog-card__icon {
  background: #e0f2f1;
  color: #004d40;
}

.catalog-card__icon svg {
  width: 24px;
  height: 24px;
}

.catalog-card__title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #212121;
}

.catalog-card__description {
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  color: #616161;
  flex: 1;
}

.catalog-card__status {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
}

.status-indicator {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.status-indicator.connected {
  background: #4caf50;
  animation: pulse 2s infinite;
}

.status-indicator.disconnected {
  background: #f44336;
}

.status-text {
  font-size: 12px;
  font-weight: 500;
  color: #757575;
}

/* Card Footer */
.catalog-card__footer {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #f5f5f5;
}

.catalog-card__endpoint {
  display: flex;
  align-items: center;
  gap: 8px;
}

.endpoint-icon {
  width: 14px;
  height: 14px;
  color: #9e9e9e;
}

.endpoint-text {
  font-size: 12px;
  font-family: "SF Mono", Monaco, "Cascadia Code", "Roboto Mono", monospace;
  color: #616161;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Overlay for Disabled Cards */
.catalog-card__overlay {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
}

.overlay-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  color: #757575;
}

.overlay-content svg {
  width: 32px;
  height: 32px;
}

/* Publishing Summary */
.publish-summary {
  background: white;
  border-radius: 12px;
  border: 1px solid #e0e0e0;
  padding: 24px;
  margin-top: 24px;
}

.summary-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
}

.summary-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #212121;
}

.summary-count {
  background: #009688;
  color: white;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
}

.summary-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 20px;
}

.summary-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: #fafafa;
  border-radius: 8px;
  transition: background 0.2s ease;
}

.summary-item:hover {
  background: #f5f5f5;
}

.summary-item__icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.summary-item__icon.local {
  background: #e8f5e9;
  color: #00796b;
}

.summary-item__icon.global {
  background: #e0f2f1;
  color: #004d40;
}

.summary-item__icon svg {
  width: 16px;
  height: 16px;
}

.summary-item__name {
  flex: 1;
  font-size: 14px;
  font-weight: 500;
  color: #212121;
}

.summary-item__type {
  font-size: 12px;
  font-weight: 600;
  color: #616161;
  text-transform: uppercase;
  padding: 4px 8px;
  border-radius: 12px;
  background: #f5f5f5;
}

.summary-hint {
  margin: 0;
  font-size: 13px;
  color: #616161;
  text-align: center;
  padding: 12px;
  background: #fafafa;
  border-radius: 8px;
  border: 1px dashed #e0e0e0;
}

/* Dialog Footer */
.dialog-footer {
  display: flex;
  gap: 16px;
  width: 100%;
}

/* Button Styles */
.btn {
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.btn--outline {
  background: transparent;
  border: 2px solid #e0e0e0;
  color: #616161;
}

.btn--outline:hover {
  border-color: #bdbdbd;
  background: #fafafa;
}

.btn--primary {
  background: #009688;
  color: white;
  border: 2px solid #009688;
}

.btn--primary:hover:not(:disabled) {
  background: #00796b;
  border-color: #00796b;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 150, 136, 0.3);
}

.btn--primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
  background: #b2dfdb;
  border-color: #b2dfdb;
}

.btn-icon {
  width: 18px;
  height: 18px;
  stroke: white;
}

.loading-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.publishing-overlay {
  position: fixed;
  inset: 0;
  background: rgba(9, 13, 27, 0.7);
  backdrop-filter: blur(6px);
  z-index: 3000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.publishing-overlay__panel {
  background: #0f172a;
  color: #e8eefc;
  padding: 28px 32px;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.35);
  border: 1px solid rgba(255, 255, 255, 0.1);
  text-align: center;
  min-width: 280px;
}

.publishing-overlay__spinner {
  width: 28px;
  height: 28px;
  border: 3px solid rgba(255, 255, 255, 0.2);
  border-top-color: #4fd1c5;
  border-radius: 50%;
  margin: 0 auto 12px;
  animation: spin 1s linear infinite;
}

.publishing-overlay__title {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 6px;
}

.publishing-overlay__subtitle {
  font-size: 13px;
  color: rgba(232, 238, 252, 0.7);
}

/* Green Header */
.green-header {
  background: #009688;
  color: white;
}

/* Animations */
@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Responsive Design */
@media (max-width: 768px) {
  .dialog-content {
    padding: 24px;
    max-height: 50vh;
  }

  .catalog-grid {
    grid-template-columns: 1fr;
  }

  .dialog-footer {
    flex-direction: column;
  }

  .btn {
    width: 100%;
    justify-content: center;
  }
}

@media (max-width: 480px) {
  .dialog-header {
    padding: 20px 24px;
  }

  .dialog-header__title {
    font-size: 20px;
  }

  .dialog-header__subtitle {
    font-size: 13px;
  }

  .catalog-card {
    min-height: 220px;
    padding: 16px;
  }
}

/* Enhanced Contract Modal Styles */
.contract-modal-header {
  background: linear-gradient(135deg, #009688 0%, #00695c 100%);
  color: white;
}

/* Configuration Section */
.configuration-section {
  background: var(--card);
  border: 1px solid var(--stroke);
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 0;
  margin-bottom: 20px;
  color: var(--txt);
  font-size: 18px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.static-field {
  padding: 10px 12px;
  background: var(--card-soft);
  border: 1px solid var(--stroke);
  border-radius: 8px;
  color: var(--txt);
  font-size: 14px;
}

.static-field.code {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 13px;
}

.endpoint-url {
  word-break: break-all;
}

/* Credentials Check */
.credentials-check {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.credential-item {
  display: flex;
  align-items: center;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid var(--stroke);
  background: var(--card-soft);
  transition: all 0.2s ease;
}

.credential-item.valid {
  border-left: 4px solid var(--ok);
  background: rgba(24, 201, 100, 0.08);
}

.credential-item.missing {
  border-left: 4px solid var(--err);
  background: rgba(255, 93, 117, 0.08);
}

.credential-icon {
  margin-right: 12px;
  font-size: 20px;
}

.credential-item.valid .credential-icon {
  color: var(--ok);
}

.credential-item.missing .credential-icon {
  color: var(--err);
}

.credential-details {
  flex: 1;
}

.credential-name {
  display: block;
  font-weight: 600;
  color: var(--txt);
}

.credential-status {
  display: block;
  font-size: 12px;
  color: var(--muted);
}

.credential-constraint {
  font-size: 11px;
  padding: 4px 8px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 4px;
  color: var(--muted);
}

.warning-message {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
  margin-top: 15px;
  background: rgba(255, 176, 32, 0.1);
  border: 1px solid rgba(255, 176, 32, 0.3);
  border-radius: 8px;
  color: var(--warn);
}

.warning-message i {
  font-size: 18px;
}

/* Progress Stages */
.contract-progress,
.transfer-progress {
  padding: 10px 0;
}

.progress-header {
  text-align: center;
  margin-bottom: 30px;
}

.progress-header h3 {
  margin: 0 0 8px;
  color: var(--txt);
}

.progress-stages,
.transfer-stages {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.stage {
  display: flex;
  align-items: flex-start;
  padding: 20px;
  border-radius: 12px;
  border: 1px solid var(--stroke);
  background: var(--card);
  transition: all 0.3s ease;
}

.stage.active {
  border-color: var(--brand);
  background: linear-gradient(
    135deg,
    rgba(108, 243, 214, 0.1),
    rgba(108, 243, 214, 0.05)
  );
  box-shadow: 0 4px 12px rgba(108, 243, 214, 0.1);
}

.stage.completed {
  border-color: rgba(24, 201, 100, 0.3);
  background: rgba(24, 201, 100, 0.05);
}

.stage-icon {
  position: relative;
  margin-right: 20px;
}

.stage-number {
  position: absolute;
  top: -8px;
  right: -8px;
  width: 24px;
  height: 24px;
  background: var(--brand);
  color: #06111d;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 800;
}

.stage.completed .stage-number {
  background: var(--ok);
  color: white;
}

.stage-icon i {
  font-size: 28px;
  color: var(--muted);
}

.stage.active .stage-icon i {
  color: var(--brand);
}

.stage.completed .stage-icon i {
  color: var(--ok);
}

.stage-content {
  flex: 1;
}

.stage-content h4 {
  margin: 0 0 8px;
  color: var(--txt);
  font-size: 16px;
}

.stage-content p {
  margin: 0 0 12px;
  color: var(--muted);
  font-size: 13px;
}

.stage-details {
  background: var(--card-soft);
  border: 1px solid var(--stroke);
  border-radius: 8px;
  padding: 12px;
  margin-top: 10px;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  margin-bottom: 6px;
  font-size: 13px;
}

.detail-item:last-child {
  margin-bottom: 0;
}

.detail-item span:first-child {
  color: var(--muted);
  font-weight: 500;
}

.detail-item code {
  background: rgba(255, 255, 255, 0.05);
  padding: 2px 6px;
  border-radius: 4px;
  font-family: ui-monospace, monospace;
  color: var(--brand);
}

.status-text {
  color: var(--warn);
  font-weight: 500;
}

.stage-status {
  margin-left: 20px;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.status-badge.processing {
  background: rgba(108, 243, 214, 0.15);
  color: var(--brand);
  border: 1px solid rgba(108, 243, 214, 0.3);
}

.status-badge.completed {
  background: rgba(24, 201, 100, 0.15);
  color: var(--ok);
  border: 1px solid rgba(24, 201, 100, 0.3);
}

.status-badge.pending {
  background: rgba(255, 176, 32, 0.15);
  color: var(--warn);
  border: 1px solid rgba(255, 176, 32, 0.3);
}

/* Progress Bar */
.progress-bar-container {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 10px;
}

.progress-bar {
  flex: 1;
  height: 8px;
  background: var(--card-soft);
  border-radius: 4px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, var(--brand), var(--brand-2));
  border-radius: 4px;
  transition: width 0.3s ease;
}

.progress-text {
  font-size: 12px;
  font-weight: 600;
  color: var(--brand);
  min-width: 40px;
}

/* Controls */
.progress-controls,
.transfer-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid var(--stroke);
}

.progress-info,
.transfer-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: var(--muted);
}

.info-item i {
  font-size: 14px;
}

/* Completion View */
.completion-view {
  padding: 10px 0;
}

.completion-header {
  text-align: center;
  margin-bottom: 30px;
}

.success-icon {
  font-size: 60px;
  color: var(--ok);
  margin-bottom: 15px;
}

.completion-details {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.detail-card {
  background: var(--card);
  border: 1px solid var(--stroke);
  border-radius: 12px;
  padding: 20px;
}

.detail-card h4 {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 0;
  margin-bottom: 20px;
  color: var(--txt);
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 15px;
}

.detail-item {
  display: flex;
  flex-direction: column;
}

.detail-item .label {
  font-size: 12px;
  color: var(--muted);
  margin-bottom: 4px;
}

.detail-item .value {
  font-size: 14px;
  color: var(--txt);
  word-break: break-all;
}

.detail-item .value.code {
  font-family: ui-monospace, monospace;
  background: var(--card-soft);
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 12px;
}

.status-completed {
  color: var(--ok);
  font-weight: 600;
}

.data-preview-section {
  margin-top: 20px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.data-stats {
  display: flex;
  gap: 15px;
}

.stat {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: var(--muted);
}

.data-actions {
  display: flex;
  gap: 10px;
  margin-top: 15px;
  justify-content: flex-end;
}

.completion-actions {
  display: flex;
  justify-content: space-between;
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid var(--stroke);
}

/* Error View */
.error-view {
  padding: 10px 0;
}

.error-header {
  text-align: center;
  margin-bottom: 30px;
}

.error-icon {
  font-size: 60px;
  color: var(--err);
  margin-bottom: 15px;
}

.error-details {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.error-card {
  background: rgba(255, 93, 117, 0.08);
  border: 1px solid rgba(255, 93, 117, 0.3);
  border-radius: 12px;
  padding: 20px;
}

.error-message {
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid var(--stroke);
  border-radius: 8px;
  padding: 15px;
  color: var(--err);
  font-family: ui-monospace, monospace;
  font-size: 12px;
  white-space: pre-wrap;
  max-height: 200px;
  overflow-y: auto;
}

.troubleshooting {
  background: var(--card);
  border: 1px solid var(--stroke);
  border-radius: 12px;
  padding: 20px;
}

.troubleshooting h4 {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 0;
  margin-bottom: 15px;
  color: var(--txt);
}

.troubleshooting ul {
  margin: 0;
  padding-left: 20px;
  color: var(--muted);
}

.troubleshooting li {
  margin-bottom: 8px;
  font-size: 13px;
}

.troubleshooting li:last-child {
  margin-bottom: 0;
}

.error-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid var(--stroke);
}

/* Modal Footer */
.footer-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.action-spacer {
  flex: 1;
}

/* Responsive */
@media (max-width: 768px) {
  .stage {
    flex-direction: column;
    align-items: stretch;
  }

  .stage-icon {
    margin-right: 0;
    margin-bottom: 15px;
    align-self: center;
  }

  .stage-status {
    margin-left: 0;
    margin-top: 15px;
    align-self: center;
  }

  .detail-grid {
    grid-template-columns: 1fr;
  }

  .completion-actions,
  .error-actions {
    flex-direction: column;
    gap: 10px;
  }

  .progress-controls,
  .transfer-controls {
    flex-direction: column;
    gap: 15px;
    align-items: stretch;
  }

  .footer-actions {
    flex-direction: column;
    gap: 10px;
  }

  .footer-actions .btn {
    width: 100%;
  }
}

.global-metadata-section {
  margin-top: 20px;
  padding: 16px;
  border-radius: 14px;
  border: 1px solid #d9e6dd;
  background: linear-gradient(180deg, #f7fbf8 0%, #f2f7f4 100%);
}

.metadata-grid {
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 16px;
  margin-top: 12px;
}

.metadata-panel {
  border-radius: 12px;
  border: 1px solid #dfe9e2;
  background: #ffffff;
  padding: 14px;
}

.metadata-panel--wide {
  background: #f9fbfa;
}

.metadata-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.metadata-panel__header h4 {
  margin: 0;
  font-size: 0.95rem;
  color: #123a2a;
}

.metadata-pill {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 4px 10px;
  border-radius: 999px;
  background: #eef5f1;
  color: #2c5b44;
}

.metadata-pill--accent {
  background: #e8f2ff;
  color: #1f4b86;
}

.metadata-table {
  display: grid;
  gap: 8px;
}

.metadata-row {
  display: grid;
  grid-template-columns: 140px 1fr;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 10px;
  background: #f5f8f6;
}

.metadata-key {
  font-weight: 600;
  color: #234238;
  font-size: 0.82rem;
}

.metadata-value {
  color: #1f2937;
  font-size: 0.85rem;
  word-break: break-word;
}

.empty-note {
  font-size: 0.8rem;
  color: #6b7b72;
  padding: 6px 2px;
}

.global-metadata-card {
  margin-top: 12px;
  padding: 12px;
  border-radius: 12px;
  border: 1px solid #d5e4da;
  background: #ffffff;
}

.global-metadata-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 10px;
}

.global-metadata-card__header h4 {
  margin: 0;
  color: #183022;
  font-size: 0.98rem;
}

.metadata-status {
  font-size: 0.75rem;
  font-weight: 600;
  color: #3b6f58;
  background: #eef6f1;
  padding: 4px 10px;
  border-radius: 999px;
}

.global-metadata-card__header small {
  color: #6a7b72;
  font-size: 0.78rem;
}

.global-metadata-row {
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  gap: 8px;
  align-items: center;
  margin-bottom: 8px;
}

.global-metadata-row--removed {
  opacity: 0.6;
}

.global-metadata-row .custom-input {
  min-height: 38px;
}

.field-label {
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 0.85rem;
  color: #1f2f27;
}

.field-meta {
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 0.75rem;
  color: #6a7b72;
}

.field-default {
  font-weight: 600;
}

.field-removed-note {
  color: #a16207;
  font-weight: 700;
}

.field-label small {
  font-size: 0.7rem;
  color: #2f5d49;
  font-weight: 600;
  text-transform: uppercase;
}

.field-remove-btn {
  margin-top: 4px;
  align-self: flex-start;
  border: 1px solid #f1c084;
  color: #9a5800;
  background: #fff7ed;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 0.68rem;
  font-weight: 700;
  cursor: pointer;
}

.field-remove-btn:hover {
  border-color: #f59e0b;
  color: #854d0e;
}

.payload-preview {
  margin-top: 20px;
  padding: 16px;
  border-radius: 14px;
  border: 1px solid #d5e4da;
  background: #ffffff;
}

.payload-code {
  margin: 0;
  margin-top: 12px;
  padding: 14px;
  border-radius: 12px;
  background: #101827;
  color: #d1d5db;
  font-size: 0.78rem;
  max-height: 320px;
  overflow: auto;
}

@media (max-width: 900px) {
  .global-metadata-row {
    grid-template-columns: 1fr;
  }

  .metadata-grid {
    grid-template-columns: 1fr;
  }

  .metadata-row {
    grid-template-columns: 1fr;
  }
}
</style>
